import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  Paper,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Tooltip,
  Autocomplete,
  Link,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import React, { useState, useEffect, useRef, useCallback, useImperativeHandle } from 'react';
import { formatDate, formatCalendarDateIso, formatDateStandard, isUtcCalendarDatePassed, parseUtcDate } from '../utils/dateFormat';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PrintIcon from '@mui/icons-material/Print';
import RateReviewIcon from '@mui/icons-material/RateReview';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { isViewableContentType, openBlobInNewTab } from '../utils/documentViewer';
import DocumentUploadDialog from '../components/DocumentUploadDialog';
import SupportingDocumentsTable from '../components/SupportingDocumentsTable';
import { API_ENDPOINTS } from '../constants/api';
import type { ReviewAuditEvent } from '../types';
import type { IdpQuarterlyReview } from '../types/idp';
import { idpService } from '../services/idpService';
import AppLayout from '../components/AppLayout';
import ReviewHelpSidebar from '../components/ReviewHelpSidebar';
import { useAgentContext } from '../contexts/AgentContext';
import { usePageAgentContext } from '../hooks/usePageAgentContext';
import { AppDatePicker } from '../components/AppDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { createShouldDisableDate } from '../utils/dateValidation';
import { useDateLocale } from '../hooks/useDateLocale';
import { usePublicHolidays } from '../hooks/usePublicHolidays';
import { formatReviewStatus, getReviewStatusColor, getReviewStatusFilterKey } from '../utils/reviewStatus';
import { getLevelCriteria, normaliseRating, getRatingColor } from '../utils/reviewRatingUtils';
import { REVIEW_COMMENT_DISPLAY_MAX_LEN, truncateReviewComment } from '../utils/reviewCommentTruncate';
import { ObjectiveMeasureRatingCard } from '../components/ObjectiveMeasureRatingCard';
import { BehaviouralMeasureRatingCard } from '../components/BehaviouralMeasureRatingCard';
import { getApiErrorMessage } from '../utils/apiError';
import { couldNotLoad, couldNotSubmit, couldNotUpdate, couldNotApprove, couldNotReject, couldNotFinalise, couldNotWithdraw, couldNotAdd, couldNotReopen, couldNotAcknowledge, documentCopy, reviewCycleCopy } from '../utils/userFacingCopy';

interface ReviewObjectiveRating {
  id: string;
  objectiveMeasureId: string;
  objectiveMeasureName: string;
  level1Criteria?: string;
  level2Criteria?: string;
  level3Criteria?: string;
  employeeRating?: number | string;
  employeeComment?: string;
  managerRating?: number | string;
  managerComment?: string;
}

interface ReviewBehaviouralRating {
  id: string;
  behaviouralMeasureId: string;
  behaviouralMeasureName: string;
  employeeRating?: number;
  employeeComment?: string;
  managerRating?: number;
  managerComment?: string;
}

interface PeerNomination {
  id: string;
  nominatedPeerId: string;
  nominatedPeerName: string;
  nominatedPeerEmail?: string | null;
  isApproved: boolean;
  approvedAt?: string;
  isRejected: boolean;
  rejectedAt?: string;
  rejectionReason?: string;
  hasResponded?: boolean;
}

interface Review {
  id: string;
  reviewCycleId: string;
  reviewCycleName?: string;
  /** Last day (inclusive) the employee may save or submit their self-review. */
  employeeCutoff?: string;
  employeeId: string;
  employeeName?: string;
  managerId?: string;
  managerName?: string;
  status: string | number;
  createdAt?: string;
  employeeDevelopmentAreas?: string;
  selfReviewSubmittedAt?: string;
  managerOverallAssessment?: string;
  managerReviewSubmittedAt?: string;
  discussionDate?: string;
  actualDiscussionDate?: string;
  discussionAcknowledgedAt?: string;
  finalisedAt?: string;
  managerReviewedIdpAt?: string;
  managerIdpReviewComments?: string;
  employeeAcknowledgedIdpReviewAt?: string;
  employeeIdpReviewComments?: string;
  disputeReason?: string;
  disputedAt?: string;
  disputedById?: string;
  excludeRequestedAt?: string;
  excludeRequestedById?: string;
  excludeReason?: string;
  excludeApprovedAt?: string;
  excludeApprovedById?: string;
  objectiveAverage?: number;
  /** Server-computed; aligned with objective weighting when the official objective rollup exists. */
  employeeSelfRatingAverage?: number;
  behaviouralAverage?: number;
  combinedRating?: number;
  finalRating?: number;
  objectiveRatings: ReviewObjectiveRating[];
  behaviouralRatings: ReviewBehaviouralRating[];
  peerNominations: PeerNomination[];
  peerRatings?: PeerRating[];
  peerObjectiveMeasureIds?: string[];
  peerObjectiveRatings?: PeerObjectiveRatingView[];
  /** Anonymous peer averages — visible only when enough peers have rated to protect anonymity. */
  anonymousPeerFeedback?: AnonymousPeerFeedbackSummary;
  /** True if the employee has an IDP for the review cycle's year (required before submitting self-review). */
  employeeHasIdpForCycleYear?: boolean;
  supportingDocuments?: SupportingDocument[];
  /** Admin / Senior Manager only: employee feedback attestation (0 = Regular, 1 = Some, 2 = None). Manager never sees this. */
  feedbackAttestation?: number | null;
  feedbackAttestationComment?: string | null;
}

interface AnonymousPeerFeedbackSummary {
  isVisible: boolean;
  submittedPeerCount: number;
  visibilityThreshold: number;
  objectiveAverages: AnonymousPeerObjectiveAverage[];
}

interface AnonymousPeerObjectiveAverage {
  objectiveMeasureId: string;
  objectiveMeasureName: string;
  averageRating?: number | null;
  ratingCount: number;
  hasEnoughResponses: boolean;
}

interface PeerObjectiveRatingView {
  id: string;
  objectiveMeasureId: string;
  objectiveMeasureName: string;
  raterId?: string;
  raterName?: string;
  rating?: number;
  comment?: string;
  submittedAt?: string;
}

interface SupportingDocument {
  id: string;
  reviewId: string;
  uploadedById: string;
  uploadedByName: string;
  fileName: string;
  contentType: string;
  fileSize: number;
  description?: string;
  uploadedAt: string;
  isEmployeeDocument: boolean;
}

interface PeerRating {
  id?: string;
  raterId?: string;
  raterName?: string;
  behaviouralMeasureId: string;
  behaviouralMeasureName?: string;
  rating?: number;
  comment?: string;
  submittedAt?: string;
}

interface ObjectiveRatingInput {
  objectiveMeasureId: string;
  rating: number | null;
  comment: string;
}

// Removed hardcoded competencyNames - now using behaviouralMeasureName from API

const ratingLabels: Record<number, string> = {
  1: 'Development Required',
  2: 'Meets Expectation',
  3: 'Exceeds Expectation',
};

/** Controls and navigation — hidden when printing; printed content follows the same visibility rules as the screen. */
const printHiddenSx = { '@media print': { display: 'none !important' } };

/** Table cell: truncated on screen, full text in print only (same data as on-screen tooltips). */
function CommentTableCell({ truncated }: { truncated: { display: string; full: string } }) {
  const { display, full } = truncated;
  return (
    <TableCell sx={{ maxWidth: 200 }}>
      <Tooltip title={full} placement="top">
        <Typography
          component="span"
          variant="body2"
          color="text.secondary"
          noWrap
          sx={{ maxWidth: 200, display: 'block', '@media print': { display: 'none' } }}
        >
          {display}
        </Typography>
      </Tooltip>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          display: 'none',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          '@media print': { display: 'block' },
        }}
      >
        {full.trim() ? full : '—'}
      </Typography>
    </TableCell>
  );
}

/** Manager review dialog with local form state so typing doesn't re-render the whole page. */
interface ManagerReviewDialogProps {
  open: boolean;
  onClose: () => void;
  review: Review | null;
  onSubmit: (payload: { objectiveRatings: ObjectiveRatingInput[]; overallAssessment: string; discussionDate: string }) => Promise<void>;
  submitting: boolean;
  submitError: string;
  onDismissError: () => void;
}

/** Form body keyed by review.id so it remounts with fresh state when dialog opens (no setState-in-effect). */
interface ManagerReviewFormContentProps {
  review: Review;
  onSubmit: (payload: { objectiveRatings: ObjectiveRatingInput[]; overallAssessment: string; discussionDate: string }) => Promise<void>;
  submitError: string;
  onDismissError: () => void;
}

const ManagerReviewFormContent = React.forwardRef<
  { handleSubmit: () => Promise<void> },
  ManagerReviewFormContentProps
>(function ManagerReviewFormContent({ review, onSubmit, submitError, onDismissError }, ref) {
  const country = useDateLocale();
  const { isHoliday } = usePublicHolidays(country);
  const shouldDisableDate = createShouldDisableDate(isHoliday);
  const [objectiveRatings, setObjectiveRatings] = useState<ObjectiveRatingInput[]>(() =>
    review.objectiveRatings.map(r => {
      const raw = r.managerRating;
      const isNa = raw === 'NotApplicable' || raw === 0;
      return {
        objectiveMeasureId: r.objectiveMeasureId,
        rating: isNa ? 0 : (normaliseRating(raw as number | string | undefined) ?? null),
        comment: r.managerComment || ''
      };
    })
  );
  const [overallAssessment, setOverallAssessment] = useState(() => review.managerOverallAssessment || '');
  const [discussionDate, setDiscussionDate] = useState<Date | null>(() =>
    parseUtcDate(review.discussionDate)
  );
  const [managerReviewedIdp, setManagerReviewedIdp] = useState(() => !!review.managerReviewedIdpAt);
  const [managerIdpReviewComments, setManagerIdpReviewComments] = useState(() => review.managerIdpReviewComments || '');
  const [errorIndices, setErrorIndices] = useState<number[] | null>(null);
  const [idpReviewError, setIdpReviewError] = useState<string | null>(null);
  const firstErrorRef = useRef<HTMLDivElement | null>(null);

  const handleSubmit = useCallback(async () => {
    const unratedIndices = objectiveRatings
      .map((r, i) => ({ r, index: i }))
      .filter(({ r }) => r.rating == null)
      .map(({ index }) => index);
    if (unratedIndices.length > 0) {
      setErrorIndices(unratedIndices);
      return;
    }
    const naNoReasonIndices = objectiveRatings
      .map((r, i) => ({ r, index: i }))
      .filter(({ r }) => r.rating === 0 && !r.comment.trim())
      .map(({ index }) => index);
    if (naNoReasonIndices.length > 0) {
      setErrorIndices(naNoReasonIndices);
      return;
    }
    const hasIdp = review.employeeHasIdpForCycleYear === true;
    if (hasIdp && !managerReviewedIdp) {
      setIdpReviewError('You must confirm that you have reviewed the employee\'s IDP as part of this review.');
      return;
    }
    setIdpReviewError(null);
    setErrorIndices(null);
    const discussionDateStr = discussionDate ? formatCalendarDateIso(discussionDate) : '';
    await onSubmit({
      objectiveRatings,
      overallAssessment,
      discussionDate: discussionDateStr,
      managerReviewedIdp,
      managerIdpReviewComments: managerIdpReviewComments.trim() || undefined
    });
  }, [objectiveRatings, overallAssessment, discussionDate, managerReviewedIdp, managerIdpReviewComments, review.employeeHasIdpForCycleYear, onSubmit]);

  useImperativeHandle(ref, () => ({ handleSubmit }), [handleSubmit]);

  // When we set error indices, scroll to first problem and focus its dropdown
  useEffect(() => {
    if (errorIndices?.length && firstErrorRef.current) {
      const el = firstErrorRef.current;
      const timer = setTimeout(() => {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const trigger = el.querySelector('[role="button"]') as HTMLElement | null;
        if (trigger) trigger.focus();
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [errorIndices]);

  return (
    <>
      {submitError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={onDismissError}>
          {submitError}
        </Alert>
      )}

      {(objectiveRatings ?? []).length > 0 && (
        <>
          <Typography variant="h6" fontWeight="600" gutterBottom sx={{ mt: 2 }}>
            Objective Measures
          </Typography>
          {(objectiveRatings ?? []).map((rating, index) => {
            const measure = review?.objectiveRatings[index];
            return (
              <ObjectiveMeasureRatingCard
                key={measure?.objectiveMeasureId ?? index}
                measureName={measure?.objectiveMeasureName ?? 'Objective measure'}
                level1Criteria={getLevelCriteria(measure as Record<string, string | undefined>, 1)}
                level2Criteria={getLevelCriteria(measure as Record<string, string | undefined>, 2)}
                level3Criteria={getLevelCriteria(measure as Record<string, string | undefined>, 3)}
                value={rating.rating}
                onRatingChange={(val) => {
                  setErrorIndices(null);
                  setObjectiveRatings((prev) => prev.map((r, i) => (i === index ? { ...r, rating: val } : r)));
                }}
                comment={rating.comment}
                onCommentChange={(val) =>
                  setObjectiveRatings((prev) => prev.map((r, i) => (i === index ? { ...r, comment: val } : r)))
                }
                error={errorIndices !== null && errorIndices.includes(index)}
                ratingLabel="Manager Rating *"
                commentLabel={rating.rating === 0 ? 'Reason for N/A *' : 'Manager Comment (optional)'}
                previousRating={measure?.employeeRating}
                previousComment={measure?.employeeComment}
                onClearError={() => setErrorIndices(null)}
                inputRef={errorIndices !== null && index === errorIndices[0] ? firstErrorRef : undefined}
              />
            );
          })}
        </>
      )}

        <TextField
          fullWidth
          label="Overall Assessment (optional)"
          value={overallAssessment}
          onChange={(e) => setOverallAssessment(e.target.value)}
          margin="normal"
          multiline
          rows={4}
          placeholder="Provide your overall assessment of the employee's performance..."
        />

        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <AppDatePicker
            label="Discussion Date (optional)"
            value={discussionDate}
            onChange={(d) => setDiscussionDate(d)}
            slotProps={{
              textField: {
                fullWidth: true,
                margin: 'normal',
                helperText: "Schedule when you'll discuss this review with the employee",
              },
            }}
            shouldDisableDate={shouldDisableDate}
          />
        </LocalizationProvider>

        {review.employeeHasIdpForCycleYear && (
          <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle2" fontWeight="600" gutterBottom>
              IDP reviewed as part of this review
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={managerReviewedIdp}
                  onChange={(e) => { setManagerReviewedIdp(e.target.checked); setIdpReviewError(null); }}
                  color="primary"
                />
              }
              label="I have reviewed the employee's Individual Development Plan (IDP) as part of this review"
              sx={{ display: 'block', mb: 1 }}
            />
            {idpReviewError && (
              <Typography variant="body2" color="error" sx={{ mb: 1 }}>{idpReviewError}</Typography>
            )}
            <TextField
              fullWidth
              label="IDP review comments (optional)"
              value={managerIdpReviewComments}
              onChange={(e) => setManagerIdpReviewComments(e.target.value)}
              margin="normal"
              multiline
              rows={2}
              placeholder="Any specific points about the IDP discussed in this review..."
            />
          </Box>
        )}
    </>
  );
});

function ManagerReviewDialog({ open, onClose, review, onSubmit, submitting, submitError, onDismissError }: ManagerReviewDialogProps) {
  const formRef = useRef<{ handleSubmit: () => Promise<void> }>(null);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { maxHeight: '90vh' }
      }}
    >
      <DialogTitle>Complete Manager Review</DialogTitle>
      <DialogContent>
        {open && review ? (
          <ManagerReviewFormContent
            ref={formRef}
            key={review.id}
            review={review}
            onSubmit={onSubmit}
            submitError={submitError}
            onDismissError={onDismissError}
          />
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button onClick={() => formRef.current?.handleSubmit()} variant="contained" disabled={submitting} sx={{ textTransform: 'none' }}>
          {submitting ? <CircularProgress size={24} /> : 'Submit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const DISPUTE_REASON_MAX_LENGTH = 2000;

/** Dispute dialog with local form state so typing doesn't re-render the whole page. */
interface DisputeDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (reason: string) => Promise<void>;
  submitting: boolean;
  submitError: string;
  onDismissError: () => void;
}

/** Form body only mounted when open so it gets fresh state on open (no setState-in-effect). */
function DisputeDialogFormContent({
  onSubmit,
  submitError,
  onDismissError,
  onClose,
  submitting,
}: {
  onSubmit: (reason: string) => Promise<void>;
  submitError: string;
  onDismissError: () => void;
  onClose: () => void;
  submitting: boolean;
}) {
  const [disputeReason, setDisputeReason] = useState('');
  const [disputeAcknowledged, setDisputeAcknowledged] = useState(false);
  const handleSubmit = async () => {
    const trimmed = disputeReason.trim();
    if (!trimmed || !disputeAcknowledged) return;
    await onSubmit(trimmed);
  };
  const canSubmit = disputeReason.trim().length > 0 && disputeAcknowledged;

  return (
    <>
      {submitError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={onDismissError}>
          {submitError}
        </Alert>
      )}
      <Alert severity="warning" sx={{ mb: 2 }}>
        <Typography variant="body2" fontWeight="600" gutterBottom>
          Important: This will trigger a formal HR process
        </Typography>
        <Typography variant="body2">
          By disputing this review, you are initiating a formal HR review process. The manager will be locked out of this review, and HR will work with you to resolve the dispute.
        </Typography>
      </Alert>
      <TextField
        fullWidth
        label="Dispute Reason"
        value={disputeReason}
        onChange={(e) => setDisputeReason(e.target.value.slice(0, DISPUTE_REASON_MAX_LENGTH))}
        margin="normal"
        multiline
        rows={6}
        required
        placeholder="Provide a detailed explanation of why you are disputing this review."
        helperText={`${disputeReason.length} / ${DISPUTE_REASON_MAX_LENGTH} characters`}
        inputProps={{ maxLength: DISPUTE_REASON_MAX_LENGTH }}
        autoFocus
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={disputeAcknowledged}
            onChange={(e) => setDisputeAcknowledged(e.target.checked)}
            size="small"
            sx={{ p: 0, mr: 0.5 }}
          />
        }
        label="I understand this triggers a formal HR process"
        sx={{ mt: 2, alignItems: 'flex-start', ml: 0 }}
        componentsProps={{ typography: { variant: 'body2', color: 'text.secondary' } }}
      />
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="warning"
          disabled={submitting || !canSubmit}
          sx={{ textTransform: 'none' }}
        >
          {submitting ? <CircularProgress size={24} /> : 'Submit Dispute'}
        </Button>
      </DialogActions>
    </>
  );
}

function DisputeDialog({ open, onClose, onSubmit, submitting, submitError, onDismissError }: DisputeDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Dispute Review</DialogTitle>
      <DialogContent>
        {open ? (
          <DisputeDialogFormContent
            key="dispute-form"
            onSubmit={onSubmit}
            submitError={submitError}
            onDismissError={onDismissError}
            onClose={onClose}
            submitting={submitting}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

const EXCLUDE_COMMENT_MAX_LENGTH = 2000;

/** Dialog for employee to request closing a review without rating (e.g. leave, sabbatical). */
function ExcludeDialog({
  open,
  onClose,
  onSubmit,
  submitting,
  submitError,
  onDismissError,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (comment: string) => Promise<void>;
  submitting: boolean;
  submitError: string;
  onDismissError: () => void;
}) {
  const [comment, setComment] = useState('');

  // Reset on open - defer to avoid synchronous setState in effect (cascading renders)
  useEffect(() => {
    if (open) queueMicrotask(() => setComment(''));
  }, [open]);

  const handleSubmit = async () => {
    await onSubmit(comment.trim());
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Request Close Without Rating</DialogTitle>
      <DialogContent>
        {submitError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={onDismissError}>
            {submitError}
          </Alert>
        )}
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            Use this if you need to close this review without any ratings being collected (e.g. extended leave, sabbatical, or role change). Your manager will need to approve this request.
          </Typography>
        </Alert>
        <TextField
          fullWidth
          label="Reason (optional)"
          value={comment}
          onChange={(e) => setComment(e.target.value.slice(0, EXCLUDE_COMMENT_MAX_LENGTH))}
          margin="normal"
          multiline
          rows={4}
          placeholder="Briefly explain why this review should be closed without rating."
          helperText={`${comment.length} / ${EXCLUDE_COMMENT_MAX_LENGTH} characters`}
          inputProps={{ maxLength: EXCLUDE_COMMENT_MAX_LENGTH }}
          autoFocus
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting}
          sx={{ textTransform: 'none' }}
        >
          {submitting ? <CircularProgress size={24} /> : 'Submit Request'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function ReviewDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user: currentUser, displayTimeZoneId } = useAuth();
  const { showLegacyHelp } = useAgentContext();
  const country = useDateLocale();
  const { isHoliday } = usePublicHolidays(country);
  const shouldDisableDate = createShouldDisableDate(isHoliday);
  const [review, setReview] = useState<Review | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [managerReviewDialogOpen, setManagerReviewDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string>('');
  const [acknowledgmentDialogOpen, setAcknowledgmentDialogOpen] = useState(false);
  const [actualDiscussionDate, setActualDiscussionDate] = useState<Date | null>(null);
  const [acknowledgmentConfirmed, setAcknowledgmentConfirmed] = useState(false);
  const [employeeIdpReviewComments, setEmployeeIdpReviewComments] = useState('');
  /** Feedback attestation: 0 = Regular, 1 = Some, 2 = None. Required when acknowledging; not visible to manager. */
  const [feedbackAttestation, setFeedbackAttestation] = useState<number | null>(null);
  const [feedbackAttestationComment, setFeedbackAttestationComment] = useState('');
  const [reopenConfirmDialogOpen, setReopenConfirmDialogOpen] = useState(false);
  const [finaliseConfirmDialogOpen, setFinaliseConfirmDialogOpen] = useState(false);
  const [disputeDialogOpen, setDisputeDialogOpen] = useState(false);
  const [withdrawDisputeDialogOpen, setWithdrawDisputeDialogOpen] = useState(false);
  const [excludeDialogOpen, setExcludeDialogOpen] = useState(false);
  const [excludeConfirmDialog, setExcludeConfirmDialog] = useState<'approve' | 'deny' | null>(null);
  const [uploadDocDialogOpen, setUploadDocDialogOpen] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [previewNotAvailableFileName, setPreviewNotAvailableFileName] = useState<string | null>(null);
  const [editDocDialogOpen, setEditDocDialogOpen] = useState(false);
  const [editingDoc, setEditingDoc] = useState<{ id: string; fileName: string; description: string } | null>(null);
  const [deleteConfirmDoc, setDeleteConfirmDoc] = useState<{ documentId: string; fileName: string } | null>(null);
  const [deletingDocId, setDeletingDocId] = useState<string | null>(null);

  const [idpQuarterlyReview, setIdpQuarterlyReview] = useState<IdpQuarterlyReview | null>(null);
  const [idpQuarterlyReviewLoading, setIdpQuarterlyReviewLoading] = useState(false);

  // Peer rating state
  const [peerRatingDialogOpen, setPeerRatingDialogOpen] = useState(false);
  const [peerRatings, setPeerRatings] = useState<{ behaviouralMeasureId: string; behaviouralMeasureName: string; rating?: number; comment: string }[]>([]);
  // Peer objective ratings (for approved peer to submit)
  const [peerObjectiveRatingDialogOpen, setPeerObjectiveRatingDialogOpen] = useState(false);
  const [peerObjectiveRatingForm, setPeerObjectiveRatingForm] = useState<{ objectiveMeasureId: string; objectiveMeasureName: string; rating: number; comment: string }[]>([]);
  const [peerObjectiveSubmitting, setPeerObjectiveSubmitting] = useState(false);
  const [peerObjectiveSubmitError, setPeerObjectiveSubmitError] = useState('');

  // Peer review settings
  const [addPeerDialogOpen, setAddPeerDialogOpen] = useState(false);
  const [addPeerSelectedPeers, setAddPeerSelectedPeers] = useState<{ id: string; fullName: string }[]>([]);
  const [addPeerSelectedObjectiveIds, setAddPeerSelectedObjectiveIds] = useState<string[]>([]);
  const [addPeerOptions, setAddPeerOptions] = useState<{ id: string; fullName: string }[]>([]);
  const [addPeerOptionsLoading, setAddPeerOptionsLoading] = useState(false);
  const [addPeerError, setAddPeerError] = useState('');
  const [addPeerSubmitting, setAddPeerSubmitting] = useState(false);
  const [peerReviewSettings, setPeerReviewSettings] = useState<{ allowPeerReviews: boolean; minPeerReviewers: number; maxPeerReviewers: number }>({
    allowPeerReviews: false,
    minPeerReviewers: 2,
    maxPeerReviewers: 4
  });

  // Audit trail state
  const [auditEvents, setAuditEvents] = useState<ReviewAuditEvent[]>([]);
  const [loadingAudit, setLoadingAudit] = useState(false);

  // Objective measure detail modal (read-only view)
  const [measureDetailRating, setMeasureDetailRating] = useState<ReviewObjectiveRating | null>(null);
  const [helpExpanded, setHelpExpanded] = useState(false);
  usePageAgentContext({
    pageId: 'review-detail',
    pageTitle: 'Review detail',
    visibleSections: ['objective-ratings', 'behavioural-ratings', 'peer-feedback', 'idp'],
    suggestedPrompts: [
      'What actions can I take in this review status?',
      'Explain who can see which sections on this page.',
      'What should I complete next on this review?',
    ],
  });

  useEffect(() => {
    const loadData = async () => {
      if (!id) {
        setError('Invalid review ID');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError('');

        const reviewResponse = await apiService.get<Review>(`/reviews/${id}`);

        // Get peer review settings
        const peerSettingsResponse = await apiService.get<{ allowPeerReviews: boolean; minPeerReviewers: number; maxPeerReviewers: number }>('/System/peer-review-settings').catch(() => ({ data: { allowPeerReviews: false, minPeerReviewers: 2, maxPeerReviewers: 4 } }));

        setReview(reviewResponse.data);
        setPeerReviewSettings(peerSettingsResponse.data);

        // Load audit trail
        setLoadingAudit(true);
        try {
          const auditResponse = await apiService.get<ReviewAuditEvent[]>(
            API_ENDPOINTS.REVIEWS.AUDIT_TRAIL(id)
          );
          setAuditEvents(auditResponse.data);
        } catch (err: unknown) {
          console.error('Failed to load audit trail', err);
        } finally {
          setLoadingAudit(false);
        }

        // Load IDP quarterly review
        setIdpQuarterlyReviewLoading(true);
        try {
          const quarterlyReview = await idpService.getQuarterlyReviewForReview(id);
          setIdpQuarterlyReview(quarterlyReview);
        } catch (err: unknown) {
          console.error('Failed to load IDP quarterly review', err);
          setIdpQuarterlyReview(null);
        } finally {
          setIdpQuarterlyReviewLoading(false);
        }
      } catch (err: unknown) {
        console.error('Failed to load review:', err);
        if (err && typeof err === 'object' && 'response' in err) {
          const axiosError = err as { response?: { status?: number; data?: { message?: string; redirectTo?: string } } };
          if (axiosError.response?.status === 404) {
            setError('Review not found');
          } else if (axiosError.response?.status === 401) {
            navigate('/login');
          } else if (axiosError.response?.status === 403 || axiosError.response?.status === 400) {
            const redirectTo = axiosError.response?.data?.redirectTo;
            if (typeof redirectTo === 'string' && redirectTo.trim()) {
              navigate(redirectTo.trim(), { replace: true });
              return;
            }
            setError(
              getApiErrorMessage(
                err,
                'You do not have permission to view this review. If you were asked to provide peer feedback, the review cycle deadline may have passed.'
              )
            );
          } else {
            setError(couldNotLoad('the review'));
          }
        } else {
          setError(couldNotLoad('the review'));
        }
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id, navigate]);

  const isManager = (userId: string, managerId?: string) => {
    return managerId === userId;
  };

  const canSubmitManagerReview = () => {
    if (!review || !currentUser) return false;
    const status = String(review.status).toLowerCase();
    return isManager(currentUser.id, review.managerId)
      && status === 'selfreviewcomplete';
  };

  const canEditManagerReview = () => {
    if (!review || !currentUser) return false;
    const status = String(review.status).toLowerCase();
    // Manager can only edit review in PendingDiscussion state (before employee acknowledges)
    return isManager(currentUser.id, review.managerId) && status === 'pendingdiscussion';
  };

  const canReopenReview = () => {
    if (!review || !currentUser) return false;
    const status = String(review.status).toLowerCase();
    const isAdminUser = currentUser.roles?.some((r: string) => r === 'Administrator');
    if (status === 'notsubmitted' || status === '9') {
      return !!isAdminUser;
    }
    return isManager(currentUser.id, review.managerId) &&
      (status === 'discussionacknowledged' || status === 'finalised' || status === '6');
  };

  const canAcknowledgeDiscussion = () => {
    if (!review || !currentUser) return false;
    const status = String(review.status).toLowerCase();
    return review.employeeId === currentUser.id && status === 'pendingdiscussion';
  };

  const canFinaliseReview = () => {
    if (!review || !currentUser) return false;
    const status = String(review.status).toLowerCase();
    // Manager cannot finalise disputed reviews
    if (status === 'underdispute' || status === '7') return false;
    return isManager(currentUser.id, review.managerId) && status === 'discussionacknowledged';
  };

  const canWithdrawDispute = () => {
    if (!review || !currentUser) return false;
    const status = String(review.status).toLowerCase();
    return review.employeeId === currentUser.id && status === 'underdispute' && review.disputedById === currentUser.id;
  };

  /** Employee or manager can upload supporting documents until discussion is acknowledged or review is finalised. */
  const canUploadSupportingDocuments = () => {
    if (!review || !currentUser) return false;
    const key = getReviewStatusFilterKey(review.status);
    if (key === 'discussionacknowledged' || key === 'finalised' || key === 'excluded') return false;
    return review.employeeId === currentUser.id || review.managerId === currentUser.id;
  };

  const isDisputed = () => {
    if (!review) return false;
    const status = String(review.status).toLowerCase();
    return status === 'underdispute' || status === '7';
  };

  const isExcluded = () => {
    if (!review) return false;
    const status = String(review.status).toLowerCase();
    return status === 'excluded' || status === '8';
  };

  const hasExcludeRequest = () => {
    return !!review?.excludeRequestedAt;
  };

  /** Employee can request exclude when review is NotStarted or SelfReviewInProgress and no request is already pending */
  const canRequestExclude = () => {
    if (!review || !currentUser) return false;
    if (review.employeeId !== currentUser.id) return false;
    if (hasExcludeRequest()) return false;
    const status = String(review.status).toLowerCase();
    return status === 'notstarted' || status === 'selfreviewinprogress';
  };

  /** Manager (or admin) can approve/deny an exclude request */
  const canActOnExcludeRequest = () => {
    if (!review || !currentUser) return false;
    if (!hasExcludeRequest()) return false;
    if (isExcluded()) return false;
    const isReviewManager = review.managerId === currentUser.id;
    const isAdminUser = currentUser.roles?.some((r: string) => r === 'Administrator');
    return isReviewManager || isAdminUser;
  };

  const isEmployeeCutoffPassed = () =>
    !!review?.employeeCutoff && isUtcCalendarDatePassed(review.employeeCutoff);

  const canSubmitSelfReview = () => {
    if (!review || !currentUser) return false;
    if (isEmployeeCutoffPassed()) return false;
    const status = String(review.status).toLowerCase();
    return review.employeeId === currentUser.id
      && (status === 'notstarted' || status === 'selfreviewinprogress' || status === 'selfreviewcomplete');
  };

  const isApprovedPeer = () => {
    if (!review || !currentUser?.id) return false;
    return review.peerNominations?.some(
      (pn) => pn.nominatedPeerId === currentUser.id && pn.isApproved
    ) ?? false;
  };

  const canAddPeerReviewers = () => {
    if (!review || !currentUser) return false;
    const isManager = review.managerId && currentUser.id === review.managerId;
    const isAdmin = currentUser.roles?.some((r: string) => r === 'Administrator');
    if (!isManager && !isAdmin) return false;
    const status = String(review.status).toLowerCase();
    const statusNum = typeof review.status === 'number' ? review.status : null;
    const allowed =
      status === 'selfreviewcomplete' || statusNum === 3 ||
      status === 'pendingdiscussion' || statusNum === 4 ||
      status === 'discussionacknowledged' || statusNum === 5;
    return allowed;
  };

  const canSubmitPeerObjectiveRatings = () => {
    if (!review || !currentUser) return false;
    if (!isApprovedPeer()) return false;
    const measureIds = review.peerObjectiveMeasureIds ?? [];
    if (measureIds.length === 0) return false;
    // Hide button if user has already submitted for all objectives
    const myRatings = review.peerObjectiveRatings?.filter((por) => por.raterId === currentUser.id) ?? [];
    const ratedMeasureIds = new Set(myRatings.map((r) => r.objectiveMeasureId));
    const hasSubmittedAll = measureIds.every((id) => ratedMeasureIds.has(id));
    return !hasSubmittedAll;
  };

  const handleOpenPeerObjectiveRatingDialog = () => {
    if (!review) return;
    const objectiveIds = review.peerObjectiveMeasureIds ?? [];
    const form = objectiveIds.map((objId) => {
      const or = review.objectiveRatings?.find((r) => r.objectiveMeasureId === objId);
      const existing = review.peerObjectiveRatings?.find(
        (por) => por.objectiveMeasureId === objId && por.raterId === currentUser?.id
      );
      return {
        objectiveMeasureId: objId,
        objectiveMeasureName: or?.objectiveMeasureName ?? 'Objective',
        rating: existing?.rating ?? 0,
        comment: existing?.comment ?? '',
      };
    });
    setPeerObjectiveRatingForm(form);
    setPeerObjectiveSubmitError('');
    setPeerObjectiveRatingDialogOpen(true);
  };

  const handleSubmitPeerObjectiveRatings = async () => {
    if (!review || !id) return;
    const unrated = peerObjectiveRatingForm.find((r) => !r.rating || r.rating < 1);
    if (unrated) {
      setPeerObjectiveSubmitError('Please rate all objectives.');
      return;
    }
    try {
      setPeerObjectiveSubmitting(true);
      setPeerObjectiveSubmitError('');
      await apiService.post(`/reviews/${review.id}/peer-objective-ratings`, {
        ratings: peerObjectiveRatingForm.map((r) => ({
          objectiveMeasureId: r.objectiveMeasureId,
          rating: r.rating,
          comment: r.comment || undefined,
        })),
      });
      const response = await apiService.get<Review>(`/reviews/${id}`);
      setReview(response.data);
      setPeerObjectiveRatingDialogOpen(false);
    } catch (err: unknown) {
            setPeerObjectiveSubmitError(getApiErrorMessage(err, couldNotSubmit('your peer ratings')));
    } finally {
      setPeerObjectiveSubmitting(false);
    }
  };


  const handleOpenManagerReview = () => {
    if (!review) return;
    setManagerReviewDialogOpen(true);
  };

  const handleSubmitManagerReview = async (payload: {
    objectiveRatings: ObjectiveRatingInput[];
    overallAssessment: string;
    discussionDate: string;
    managerReviewedIdp?: boolean;
    managerIdpReviewComments?: string;
  }) => {
    if (!review) return;

    const allObjectivesRated = payload.objectiveRatings.every(r => r.rating != null);
    if (!allObjectivesRated) {
      setSubmitError('Please rate all objective measures (or mark them N/A)');
      return;
    }
    const naNoReason = payload.objectiveRatings.some(r => r.rating === 0 && !r.comment.trim());
    if (naNoReason) {
      setSubmitError('Please provide a reason (in the comment) for each objective marked N/A');
      return;
    }
    if (review.employeeHasIdpForCycleYear && !payload.managerReviewedIdp) {
      setSubmitError('You must confirm that you have reviewed the employee\'s IDP as part of this review.');
      return;
    }

    try {
      setSubmitting(true);
      setSubmitError('');

      const status = String(review.status).toLowerCase();
      const isUpdate = status === 'pendingdiscussion' || status === 'discussionacknowledged' || status === '4' || status === '5';
      const endpoint = `/reviews/${review.id}/manager-review`;
      const body = {
        objectiveRatings: payload.objectiveRatings.map(r => ({
          objectiveMeasureId: r.objectiveMeasureId,
          rating: r.rating === 0 ? 'NotApplicable' : r.rating,
          comment: r.comment || undefined
        })),
        behaviouralRatings: [],
        peerRatingInclusions: [],
        overallAssessment: payload.overallAssessment || undefined,
        discussionDate: payload.discussionDate || undefined,
        managerReviewedIdp: payload.managerReviewedIdp ?? false,
        managerIdpReviewComments: payload.managerIdpReviewComments || undefined
      };

      if (isUpdate) {
        await apiService.put(endpoint, body);
      } else {
        await apiService.post(endpoint, body);
      }

      setManagerReviewDialogOpen(false);

      // Reload review data and audit trail
      const response = await apiService.get<Review>(`/reviews/${id}`);
      setReview(response.data);

      // Reload audit trail
      if (id) {
        setLoadingAudit(true);
        try {
          const auditResponse = await apiService.get<ReviewAuditEvent[]>(
            API_ENDPOINTS.REVIEWS.AUDIT_TRAIL(id)
          );
          setAuditEvents(auditResponse.data);
        } catch (err: unknown) {
          console.error('Failed to load audit trail', err);
        } finally {
          setLoadingAudit(false);
        }
      }
    } catch (err: unknown) {
            setSubmitError(getApiErrorMessage(err, couldNotSubmit('the manager review')));
    } finally {
      setSubmitting(false);
    }
  };

  const getReviewEventTypeLabel = (eventType: string) => {
    const labels: Record<string, string> = {
      SelfReviewSubmitted: 'Self Review Submitted',
      SelfReviewUpdatedAfterSubmit: 'Self Review Updated (After Submit)',
      ReviewNotSubmittedDueToCutoff: 'Not Submitted (Cutoff)',
      ManagerReviewSubmitted: 'Manager Review Submitted',
      DiscussionAcknowledged: 'Discussion Acknowledged',
      ReviewReopened: 'Review Reopened',
      ReviewFinalised: 'Review Finalised',
      DisputeSubmitted: 'Dispute Submitted',
      DisputeWithdrawn: 'Dispute Withdrawn',
      DisputeResolved: 'Dispute Resolved',
      PeerNominationApproved: 'Peer Nomination Approved',
      PeerNominationRejected: 'Peer Nomination Rejected',
      EmployeeActivatedPeerNominations: 'Peer reviewers invited (employee)',
      PeerRatingSubmitted: 'Peer Rating Submitted',
      ObjectiveMeasuresChanged: 'Objective Measures Changed',
      ExcludeRequested: 'Exclude Requested',
      ExcludeApproved: 'Exclude Approved',
      ExcludeDenied: 'Exclude Denied',
      // IDP events (shown when employee has an IDP for the review cycle year)
      IdpCreated: 'IDP: Created',
      IdpSubmitted: 'IDP: Submitted',
      IdpAcknowledged: 'IDP: Acknowledged',
      QuarterlyReviewSubmitted: 'IDP: Quarterly Review Submitted',
      IdpRevertedToDraftForReview: 'IDP: Reverted to Draft (Review)',
      IdpUpdated: 'IDP: Updated',
      SupportingDocumentUploaded: 'IDP: Supporting Document Uploaded',
      SupportingDocumentDeleted: 'IDP: Supporting Document Deleted',
      SupportingDocumentUpdated: 'IDP: Supporting Document Updated',
      ReviewSupportingDocumentUploaded: 'Supporting Document Uploaded',
      ReviewSupportingDocumentDeleted: 'Supporting Document Deleted',
      ReviewSupportingDocumentUpdated: 'Supporting Document Updated',
    };

    // If we have a custom label, use it; otherwise auto-format camelCase
    if (labels[eventType]) {
      return labels[eventType];
    }

    // Auto-format: "SelfReviewSubmitted" -> "Self Review Submitted"
    return eventType
      .replace(/([A-Z])/g, ' $1')
      .trim()
      .replace(/^./, (str) => str.toUpperCase());
  };

  const handleReopenReviewClick = () => {
    if (!review) return;
    setReopenConfirmDialogOpen(true);
  };

  const handleReopenReview = async () => {
    if (!review) return;

    setReopenConfirmDialogOpen(false);
    try {
      setSubmitting(true);
      setError('');
      await apiService.put(`/reviews/${review.id}/reopen`);

      const response = await apiService.get<Review>(`/reviews/${id}`);
      setReview(response.data);

      if (id) {
        setLoadingAudit(true);
        try {
          const auditResponse = await apiService.get<ReviewAuditEvent[]>(
            API_ENDPOINTS.REVIEWS.AUDIT_TRAIL(id)
          );
          setAuditEvents(auditResponse.data);
        } catch (err: unknown) {
          console.error('Failed to load audit trail', err);
        } finally {
          setLoadingAudit(false);
        }
      }
    } catch (err: unknown) {
            setError(getApiErrorMessage(err, couldNotReopen('the review')));
    } finally {
      setSubmitting(false);
    }
  };

  const handleAcknowledgeDiscussion = async () => {
    if (!review) return;

    try {
      setSubmitting(true);
      setSubmitError('');
      const dateStr = actualDiscussionDate ? formatCalendarDateIso(actualDiscussionDate) : undefined;
      await apiService.post(`/reviews/${review.id}/acknowledge-discussion`, {
        actualDiscussionDate: dateStr,
        employeeIdpReviewComments: employeeIdpReviewComments.trim() || undefined,
        feedbackAttestation: feedbackAttestation ?? undefined,
        feedbackAttestationComment: feedbackAttestationComment.trim() || undefined,
      });

      setAcknowledgmentDialogOpen(false);
      setActualDiscussionDate(null);
      setAcknowledgmentConfirmed(false);
      setEmployeeIdpReviewComments('');
      setFeedbackAttestation(null);
      setFeedbackAttestationComment('');

      // Reload review data and audit trail
      const response = await apiService.get<Review>(`/reviews/${id}`);
      setReview(response.data);

      // Reload audit trail
      if (id) {
        setLoadingAudit(true);
        try {
          const auditResponse = await apiService.get<ReviewAuditEvent[]>(
            API_ENDPOINTS.REVIEWS.AUDIT_TRAIL(id)
          );
          setAuditEvents(auditResponse.data);
        } catch (err: unknown) {
          console.error('Failed to load audit trail', err);
        } finally {
          setLoadingAudit(false);
        }
      }
    } catch (err: unknown) {
            setSubmitError(getApiErrorMessage(err, couldNotAcknowledge('the discussion')));
    } finally {
      setSubmitting(false);
    }
  };

  const handleFinaliseReviewClick = () => {
    if (!review) return;
    setFinaliseConfirmDialogOpen(true);
  };

  const handleFinaliseReview = async () => {
    if (!review) return;

    setFinaliseConfirmDialogOpen(false);
    try {
      setSubmitting(true);
      await apiService.put(`/reviews/${review.id}/finalise`);

      // Reload review data and audit trail
      const response = await apiService.get<Review>(`/reviews/${id}`);
      setReview(response.data);

      // Reload audit trail
      if (id) {
        setLoadingAudit(true);
        try {
          const auditResponse = await apiService.get<ReviewAuditEvent[]>(
            API_ENDPOINTS.REVIEWS.AUDIT_TRAIL(id)
          );
          setAuditEvents(auditResponse.data);
        } catch (err: unknown) {
          console.error('Failed to load audit trail', err);
        } finally {
          setLoadingAudit(false);
        }
      }
    } catch (err: unknown) {
            setError(getApiErrorMessage(err, couldNotFinalise('the review')));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDisputeReview = async (reason: string) => {
    if (!review || !reason.trim()) return;

    try {
      setSubmitting(true);
      setSubmitError('');
      await apiService.post(`/reviews/${review.id}/dispute`, { reason });

      setDisputeDialogOpen(false);

      // Reload review data
      const response = await apiService.get<Review>(`/reviews/${id}`);
      setReview(response.data);
    } catch (err: unknown) {
            setSubmitError(getApiErrorMessage(err, couldNotSubmit('your dispute')));
    } finally {
      setSubmitting(false);
    }
  };

  const handleRequestExclude = async (comment: string) => {
    if (!review) return;

    try {
      setSubmitting(true);
      setSubmitError('');
      await apiService.post(`/reviews/${review.id}/exclude/request`, { comment });

      setExcludeDialogOpen(false);

      // Reload review data
      const response = await apiService.get<Review>(`/reviews/${id}`);
      setReview(response.data);
    } catch (err: unknown) {
            setSubmitError(getApiErrorMessage(err, couldNotSubmit('your exclude request')));
    } finally {
      setSubmitting(false);
    }
  };

  const handleApproveExclude = async () => {
    if (!review) return;

    try {
      setSubmitting(true);
      setSubmitError('');
      await apiService.post(`/reviews/${review.id}/exclude/approve`);

      setExcludeConfirmDialog(null);

      // Reload review data
      const response = await apiService.get<Review>(`/reviews/${id}`);
      setReview(response.data);
    } catch (err: unknown) {
            setSubmitError(getApiErrorMessage(err, couldNotApprove('the exclude request')));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDenyExclude = async () => {
    if (!review) return;

    try {
      setSubmitting(true);
      setSubmitError('');
      await apiService.post(`/reviews/${review.id}/exclude/deny`);

      setExcludeConfirmDialog(null);

      // Reload review data
      const response = await apiService.get<Review>(`/reviews/${id}`);
      setReview(response.data);
    } catch (err: unknown) {
            setSubmitError(getApiErrorMessage(err, couldNotReject('the exclude request')));
    } finally {
      setSubmitting(false);
    }
  };

  const handleWithdrawDispute = async () => {
    if (!review) return;

    try {
      setSubmitting(true);
      setSubmitError('');
      await apiService.post(`/reviews/${review.id}/withdraw-dispute`);

      setWithdrawDisputeDialogOpen(false);

      // Reload review data
      const response = await apiService.get<Review>(`/reviews/${id}`);
      setReview(response.data);
    } catch (err: unknown) {
            setSubmitError(getApiErrorMessage(err, couldNotWithdraw('your dispute')));
    } finally {
      setSubmitting(false);
    }
  };

  const handleUploadSupportingDocument = async (files: File[], description: string) => {
    if (!review || !id) return;
    try {
      setUploadingDoc(true);
      setSubmitError('');
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        if (description.trim()) formData.append('description', description.trim());
        await apiService.post(`/reviews/${id}/supporting-documents/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      setUploadDocDialogOpen(false);
      const response = await apiService.get<Review>(`/reviews/${id}`);
      setReview(response.data);
      try {
        setLoadingAudit(true);
        const auditResponse = await apiService.get<ReviewAuditEvent[]>(
          API_ENDPOINTS.REVIEWS.AUDIT_TRAIL(id)
        );
        setAuditEvents(auditResponse.data);
      } catch (err: unknown) {
        console.error('Failed to refresh audit trail', err);
      } finally {
        setLoadingAudit(false);
      }
    } catch (err: unknown) {
            setSubmitError(getApiErrorMessage(err, documentCopy.upload));
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleDownloadSupportingDocument = async (documentId: string, fileName: string) => {
    if (!id) return;
    try {
      const response = await apiService.get(`/reviews/${id}/supporting-documents/${documentId}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: unknown) {
            setError(getApiErrorMessage(err, documentCopy.download));
    }
  };

  const handleViewSupportingDocument = async (documentId: string, fileName: string, contentType: string) => {
    if (!id) return;
    try {
      setError('');
      const response = await apiService.get(`/reviews/${id}/supporting-documents/${documentId}/download`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: contentType || 'application/octet-stream' });
      if (isViewableContentType(contentType)) {
        openBlobInNewTab(blob, contentType);
      } else {
        setPreviewNotAvailableFileName(fileName);
      }
    } catch (err: unknown) {
            setError(getApiErrorMessage(err, documentCopy.open));
    }
  };

  const handleDeleteSupportingDocument = async (documentId: string, _fileName: string) => {
    if (!id || !review) return;
    try {
      setDeletingDocId(documentId);
      setSubmitError('');
      await apiService.delete(`/reviews/${id}/supporting-documents/${documentId}`);
      setDeleteConfirmDoc(null);
      const response = await apiService.get<Review>(`/reviews/${id}`);
      setReview(response.data);
      try {
        setLoadingAudit(true);
        const auditResponse = await apiService.get<ReviewAuditEvent[]>(
          API_ENDPOINTS.REVIEWS.AUDIT_TRAIL(id)
        );
        setAuditEvents(auditResponse.data);
      } catch (err: unknown) {
        console.error('Failed to refresh audit trail', err);
      } finally {
        setLoadingAudit(false);
      }
    } catch (err: unknown) {
            setError(getApiErrorMessage(err, documentCopy.delete));
    } finally {
      setDeletingDocId(null);
    }
  };

  const handleUpdateSupportingDocument = async (documentId: string, fileName: string, description: string) => {
    if (!id || !review) return;
    try {
      setSubmitError('');
      await apiService.patch(`/reviews/${id}/supporting-documents/${documentId}`, { fileName: fileName.trim(), description: description.trim() || undefined });
      setEditDocDialogOpen(false);
      setEditingDoc(null);
      const response = await apiService.get<Review>(`/reviews/${id}`);
      setReview(response.data);
      try {
        setLoadingAudit(true);
        const auditResponse = await apiService.get<ReviewAuditEvent[]>(
          API_ENDPOINTS.REVIEWS.AUDIT_TRAIL(id)
        );
        setAuditEvents(auditResponse.data);
      } catch (err: unknown) {
        console.error('Failed to refresh audit trail', err);
      } finally {
        setLoadingAudit(false);
      }
    } catch (err: unknown) {
            setSubmitError(getApiErrorMessage(err, couldNotUpdate('the document')));
    }
  };

  useEffect(() => {
    if (!addPeerDialogOpen || !review) return;
    setAddPeerOptionsLoading(true);
    setAddPeerError('');
    apiService
      .get<{ id: string; firstName: string; lastName: string; email: string; children?: unknown[] }[]>('/users/organogram?view=all')
      .then((res) => {
        const flatten = (
          nodes: { id: string; firstName: string; lastName: string; email: string; children?: { id: string; firstName: string; lastName: string; email: string; children?: unknown[] }[] }[]
        ): { id: string; fullName: string }[] => {
          let list: { id: string; fullName: string }[] = [];
          for (const n of nodes) {
            list.push({ id: n.id, fullName: [n.firstName, n.lastName].filter(Boolean).join(' ') || 'Unknown' });
            if (n.children?.length) list = list.concat(flatten(n.children as { id: string; firstName: string; lastName: string; email: string; children?: unknown[] }[]));
          }
          return list;
        };
        const all = flatten(res.data);
        const excluded = new Set([
          review.employeeId,
          review.managerId,
          ...(review.peerNominations?.map((pn) => pn.nominatedPeerId) ?? []),
        ].filter(Boolean));
        setAddPeerOptions(all.filter((u) => !excluded.has(u.id)));
      })
      .catch(() => setAddPeerOptions([]))
      .finally(() => setAddPeerOptionsLoading(false));
  }, [addPeerDialogOpen, review]);

  const handleAddPeerRaters = async () => {
    if (!review || !id) return;
    const peerIds = addPeerSelectedPeers.map((p) => p.id);
    if (peerIds.length === 0) {
      setAddPeerError('Please select at least one peer to add.');
      return;
    }
    if (addPeerSelectedObjectiveIds.length === 0) {
      setAddPeerError('Please select at least one objective for peer review.');
      return;
    }
    try {
      setAddPeerSubmitting(true);
      setAddPeerError('');
      const response = await apiService.post<Review>(API_ENDPOINTS.REVIEWS.ADD_PEER_RATERS(id), {
        peerIds,
        peerObjectiveMeasureIds: addPeerSelectedObjectiveIds,
      });
      setReview(response.data);
      setAddPeerDialogOpen(false);
      setAddPeerSelectedPeers([]);
      setAddPeerSelectedObjectiveIds([]);
    } catch (err: unknown) {
            setAddPeerError(getApiErrorMessage(err, couldNotAdd('peer reviewers')));
    } finally {
      setAddPeerSubmitting(false);
    }
  };

  const handleSubmitPeerRating = async (behaviouralMeasureId: string) => {
    if (!review) return;

    const rating = peerRatings.find(r => r.behaviouralMeasureId === behaviouralMeasureId);
    if (!rating) return;

    // Validation: rating (0 = N/A, 1–3) and comment required (same as manager/employee)
    if (typeof rating.rating !== 'number' || rating.rating < 0 || rating.rating > 3) {
      setSubmitError(`Please select a rating for ${rating.behaviouralMeasureName}`);
      return;
    }
    if (!rating.comment || rating.comment.trim() === '') {
      setSubmitError(rating.rating === 0 ? `Please provide a reason for marking ${rating.behaviouralMeasureName} as N/A` : `Please provide a comment for ${rating.behaviouralMeasureName}`);
      return;
    }

    try {
      setSubmitting(true);
      setSubmitError('');

      await apiService.post(`/reviews/${review.id}/peer-ratings`, {
        behaviouralMeasureId,
        rating: rating.rating,
        comment: rating.comment,
      });

      // Update local state
      const updatedRatings = [...peerRatings];
      const index = updatedRatings.findIndex(r => r.behaviouralMeasureId === behaviouralMeasureId);
      if (index >= 0) {
        updatedRatings[index] = { ...rating };
      }
      setPeerRatings(updatedRatings);

      // Reload review data to get updated peer ratings
      const response = await apiService.get<Review>(`/reviews/${id}`);
      setReview(response.data);
    } catch (err: unknown) {
            setSubmitError(getApiErrorMessage(err, couldNotSubmit('your peer rating')));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh', bgcolor: 'transparent' }}>
          <CircularProgress size={60} />
        </Box>
      </AppLayout>
    );
  }

  if (error || !review) {
    return (
      <AppLayout>
        <Box sx={{ pb: 4 }}>
          <Box sx={{ maxWidth: '1400px', mx: 'auto', display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <IconButton
              onClick={() => navigate(-1)}
              sx={{ '&:hover': { bgcolor: 'action.hover' } }}
              title="Back"
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" component="h1" fontWeight="bold">
              Review Details
            </Typography>
          </Box>

      {/* Dispute Banner */}
      {isDisputed() && review.employeeId === currentUser?.id && (
        <Box sx={{ maxWidth: '1400px', mx: 'auto', px: 3, mb: 2 }}>
          <Alert
            severity="warning"
            action={
              canWithdrawDispute() ? (
                <Box sx={printHiddenSx} component="span">
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => setWithdrawDisputeDialogOpen(true)}
                    sx={{ textTransform: 'none' }}
                  >
                    Withdraw Dispute
                  </Button>
                </Box>
              ) : undefined
            }
            sx={{ mb: 2 }}
          >
            <Typography variant="body1" fontWeight="600" gutterBottom>
              Review Under Dispute
            </Typography>
            <Typography variant="body2">
              You disputed this review on {review.disputedAt ? formatDate(review.disputedAt, { includeTime: true }) : 'unknown date'}. 
              HR is currently reviewing your dispute. The manager has been locked out of this review until the dispute is resolved.
            </Typography>
          </Alert>
        </Box>
      )}

      {/* Excluded Banner */}
      {isExcluded() && (
        <Box sx={{ maxWidth: '1400px', mx: 'auto', px: 3, mb: 2 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body1" fontWeight="600" gutterBottom>
              Review Excluded
            </Typography>
            <Typography variant="body2">
              This review has been closed without a rating{review.excludeReason ? `: ${review.excludeReason}` : '.'}{' '}
              {review.excludeApprovedAt && `Approved on ${formatDate(review.excludeApprovedAt, { includeTime: true })}.`}
            </Typography>
          </Alert>
        </Box>
      )}

      {/* Exclude Request Pending - Employee View */}
      {hasExcludeRequest() && !isExcluded() && review.employeeId === currentUser?.id && (
        <Box sx={{ maxWidth: '1400px', mx: 'auto', px: 3, mb: 2 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body1" fontWeight="600" gutterBottom>
              Exclude Request Pending
            </Typography>
            <Typography variant="body2">
              You requested to close this review without a rating{review.excludeReason ? ` (${review.excludeReason})` : ''}. Waiting for manager approval.
            </Typography>
          </Alert>
        </Box>
      )}

      {/* Exclude Request Pending - Manager View */}
      {hasExcludeRequest() && !isExcluded() && canActOnExcludeRequest() && (
        <Box sx={{ maxWidth: '1400px', mx: 'auto', px: 3, mb: 2 }}>
          <Alert
            severity="warning"
            action={
              <Box sx={{ display: 'flex', gap: 1, ...printHiddenSx }}>
                <Button
                  color="success"
                  size="small"
                  variant="contained"
                  onClick={() => setExcludeConfirmDialog('approve')}
                  disabled={submitting}
                  sx={{ textTransform: 'none' }}
                >
                  Approve
                </Button>
                <Button
                  color="inherit"
                  size="small"
                  variant="outlined"
                  onClick={() => setExcludeConfirmDialog('deny')}
                  disabled={submitting}
                  sx={{ textTransform: 'none' }}
                >
                  Deny
                </Button>
              </Box>
            }
            sx={{ mb: 2 }}
          >
            <Typography variant="body1" fontWeight="600" gutterBottom>
              Exclude Request Pending
            </Typography>
            <Typography variant="body2">
              The employee has requested to close this review without a rating{review.excludeReason ? `: ${review.excludeReason}` : '.'}
            </Typography>
          </Alert>
        </Box>
      )}

      {/* Manager Read-Only Message for Disputed Reviews */}
      {isDisputed() && isManager(currentUser?.id, review.managerId) && (
        <Box sx={{ maxWidth: '1400px', mx: 'auto', px: 3, mb: 2 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body1" fontWeight="600" gutterBottom>
              Review Under Dispute
            </Typography>
            <Typography variant="body2">
              This review has been disputed by the employee and is currently under HR review. 
              You cannot edit or finalise this review until HR resolves the dispute.
            </Typography>
          </Alert>
        </Box>
      )}

      <Box sx={{ maxWidth: '1400px', mx: 'auto', px: 3 }}>
          <Alert severity="error">{error || 'Review not found'}</Alert>
        </Box>
      </Box>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Box
        className="review-detail-print"
        sx={{ maxWidth: '1400px', mx: 'auto', pb: 4, '@media print': { maxWidth: '100%' } }}
      >
        {/* Page title row - aligned with My Reviews, Your team and their teams, Profile */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <IconButton onClick={() => navigate(-1)} sx={{ ml: -1, ...printHiddenSx }} title="Back">
            <ArrowBackIcon />
          </IconButton>
          <RateReviewIcon
            sx={{ fontSize: 32, color: 'primary.main', alignSelf: 'flex-start', mt: 0.5, ...printHiddenSx }}
          />
          <Typography variant="h4" component="h1" fontWeight="bold" sx={{ mb: 0, flex: 1 }}>
            Review Details
          </Typography>
          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', ...printHiddenSx }}>
            {canSubmitSelfReview() && review && (
              <Button
                variant="contained"
                startIcon={<PersonAddIcon />}
                onClick={() => navigate(`/reviews/${review.id}/self-review`)}
                sx={{ textTransform: 'none' }}
              >
                {(() => {
                  const status = String(review.status).toLowerCase();
                  if (status === 'notstarted') return 'Start Self Review';
                  return 'Edit Self-Review';
                })()}
              </Button>
            )}
            {canSubmitManagerReview() && (
              <Button variant="contained" startIcon={<RateReviewIcon />} onClick={handleOpenManagerReview} sx={{ textTransform: 'none' }}>
                Complete Manager Review
              </Button>
            )}
            {canEditManagerReview() && !canSubmitManagerReview() && (
              <Button variant="contained" startIcon={<RateReviewIcon />} onClick={handleOpenManagerReview} sx={{ textTransform: 'none' }}>
                Edit Manager Review
              </Button>
            )}
            {canReopenReview() && (
              <Button variant="contained" startIcon={<RateReviewIcon />} onClick={handleReopenReviewClick} disabled={submitting} color="warning" sx={{ textTransform: 'none' }}>
                Reopen Review
              </Button>
            )}
            {canAcknowledgeDiscussion() && (
              <Button
                variant="contained"
                startIcon={<CheckCircleIcon />}
                onClick={() => {
                  setAcknowledgmentConfirmed(false);
                  setFeedbackAttestation(null);
                  setFeedbackAttestationComment('');
                  setAcknowledgmentDialogOpen(true);
                }}
                color="info"
                sx={{ textTransform: 'none' }}
              >
                Acknowledge Discussion
              </Button>
            )}
            {canFinaliseReview() && (
              <Button variant="contained" startIcon={<CheckCircleIcon />} onClick={handleFinaliseReviewClick} disabled={submitting} color="success" sx={{ textTransform: 'none' }}>
                Finalise Review
              </Button>
            )}
            {canSubmitPeerObjectiveRatings() && (
              <Button variant="outlined" startIcon={<RateReviewIcon />} onClick={handleOpenPeerObjectiveRatingDialog} sx={{ textTransform: 'none' }}>
                Start Peer Rating
              </Button>
            )}
            {canRequestExclude() && (
              <Button
                variant="outlined"
                color="inherit"
                onClick={() => setExcludeDialogOpen(true)}
                sx={{ textTransform: 'none' }}
              >
                Request Close Without Rating
              </Button>
            )}
            <Button
              variant="outlined"
              color="primary"
              startIcon={<PrintIcon />}
              onClick={() => window.print()}
              sx={{ textTransform: 'none' }}
            >
              Print
            </Button>
          </Box>
        </Box>

        {/* Excluded Banner - shown when review is closed without rating */}
        {isExcluded() && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body1" fontWeight="600" gutterBottom>
              Review Excluded
            </Typography>
            <Typography variant="body2">
              This review has been closed without a rating{review.excludeReason ? `: ${review.excludeReason}` : '.'}{' '}
              {review.excludeApprovedAt && `Approved on ${formatDate(review.excludeApprovedAt, { includeTime: true })}.`}
            </Typography>
          </Alert>
        )}

        {/* Exclude Request Pending - Employee View */}
        {hasExcludeRequest() && !isExcluded() && review.employeeId === currentUser?.id && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body1" fontWeight="600" gutterBottom>
              Exclude Request Pending
            </Typography>
            <Typography variant="body2">
              You requested to close this review without a rating{review.excludeReason ? ` (${review.excludeReason})` : ''}. Waiting for manager approval.
            </Typography>
          </Alert>
        )}

        {/* Exclude Request Pending - Manager/Admin View - Approve or Deny */}
        {hasExcludeRequest() && !isExcluded() && canActOnExcludeRequest() && (
          <Alert
            severity="warning"
            action={
              <Box sx={{ display: 'flex', gap: 1, ...printHiddenSx }}>
                <Button
                  color="success"
                  size="small"
                  variant="contained"
                  onClick={() => setExcludeConfirmDialog('approve')}
                  disabled={submitting}
                  sx={{ textTransform: 'none' }}
                >
                  Approve
                </Button>
                <Button
                  color="inherit"
                  size="small"
                  variant="outlined"
                  onClick={() => setExcludeConfirmDialog('deny')}
                  disabled={submitting}
                  sx={{ textTransform: 'none' }}
                >
                  Deny
                </Button>
              </Box>
            }
            sx={{ mb: 3 }}
          >
            <Typography variant="body1" fontWeight="600" gutterBottom>
              Exclude Request Pending
            </Typography>
            <Typography variant="body2">
              The employee has requested to close this review without a rating{review.excludeReason ? `: ${review.excludeReason}` : '.'}
            </Typography>
          </Alert>
        )}

        {submitError && !selfReviewDialogOpen && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setSubmitError('')}>
            {submitError}
          </Alert>
        )}

        {review && currentUser?.id === review.employeeId && isEmployeeCutoffPassed() && String(review.status).toLowerCase() !== 'notsubmitted' && (
          <Alert severity="info" sx={{ mb: 3 }}>
            {reviewCycleCopy.employeeCutoffPassed(formatDateStandard(review.employeeCutoff))}
          </Alert>
        )}
        {review && String(review.status).toLowerCase() === 'notsubmitted' && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            {currentUser?.id === review.employeeId
              ? reviewCycleCopy.selfReviewNotSubmittedEmployee
              : reviewCycleCopy.selfReviewNotSubmittedViewer}
          </Alert>
        )}
        {canSubmitSelfReview() && review?.employeeHasIdpForCycleYear === false && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            You must create an Individual Development Plan (IDP) for this year before you can submit your self-review. Go to My Development Plan to create one.
          </Alert>
        )}

        {/* Review Header Card */}
        <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.08)', borderRadius: 2, mb: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Box>
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                  {review.reviewCycleName || 'Review'}
                </Typography>
                <Typography variant="body1" color="text.secondary" gutterBottom>
                  <strong>Employee:</strong> {review.employeeName || 'N/A'}
                </Typography>
                {review.managerName && (
                  <Typography variant="body1" color="text.secondary">
                    <strong>Manager:</strong> {review.managerName}
                  </Typography>
                )}
              </Box>
              <Chip
                label={formatReviewStatus(review.status)}
                color={getReviewStatusColor(review.status)}
                size="medium"
                sx={{ fontWeight: 'bold' }}
              />
            </Box>
          </CardContent>
        </Card>

        {/* Summary Scores – hidden for peer reviewers (they rate objectives and must not see scores) */}
        {!isApprovedPeer() && (() => {
          const employeeSelfAvg = review.employeeSelfRatingAverage;
          const selfRatingLabel =
            employeeSelfAvg != null
              ? employeeSelfAvg < 1.5
                ? ratingLabels[1]
                : employeeSelfAvg < 2.5
                  ? ratingLabels[2]
                  : ratingLabels[3]
              : null;
          return (
            <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.08)', borderRadius: 2, mb: 3 }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h6" fontWeight="600" gutterBottom>
                  Summary Scores
                </Typography>
                <Grid container spacing={3} sx={{ mt: 1 }}>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Paper sx={{ p: 3, textAlign: 'center', bgcolor: '#fff8f0', minHeight: 120, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                      <Typography variant="body1" fontWeight="bold" color="text.secondary" gutterBottom>
                        Your self-rating
                      </Typography>
                      {employeeSelfAvg != null ? (
                        <>
                          <Typography variant="h3" fontWeight="bold" color="warning.dark">
                            {employeeSelfAvg.toFixed(1)}
                          </Typography>
                          {selfRatingLabel && (
                            <Typography variant="body1" color="text.secondary">
                              {selfRatingLabel}
                            </Typography>
                          )}
                        </>
                      ) : (
                        <Typography component="em" variant="body1" color="text.secondary" sx={{ fontStyle: 'italic', display: 'block' }}>
                          Complete and submit your self-review to see your average.
                        </Typography>
                      )}
                    </Paper>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Paper sx={{ p: 3, textAlign: 'center', bgcolor: '#f0f4ff', minHeight: 120, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                      <Typography variant="body1" fontWeight="bold" color="text.secondary" gutterBottom>
                        Objective Average
                      </Typography>
                      {review.objectiveAverage != null ? (
                        <>
                          <Typography variant="h3" fontWeight="bold" color="primary.main">
                            {review.objectiveAverage.toFixed(1)}
                          </Typography>
                          <Typography variant="body1" color="text.secondary">
                            {review.behaviouralRatings?.length ? '(60% weight)' : '(100% weight)'}
                          </Typography>
                        </>
                      ) : (
                        <Typography component="em" variant="body1" color="text.secondary" sx={{ fontStyle: 'italic', display: 'block' }}>
                          Available after your manager completes their review.
                        </Typography>
                      )}
                    </Paper>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Paper sx={{ p: 3, textAlign: 'center', bgcolor: '#f0fff4', minHeight: 120, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                      <Typography variant="body1" fontWeight="bold" color="text.secondary" gutterBottom>
                        Overall Rating
                      </Typography>
                      {review.combinedRating != null ? (
                        <>
                          <Typography variant="h3" fontWeight="bold" color="success.main">
                            {review.combinedRating.toFixed(1)}
                          </Typography>
                          {normaliseRating(review.finalRating as number | string | undefined) != null && (
                            <Typography variant="body1" color="text.secondary">
                              {ratingLabels[normaliseRating(review.finalRating as number | string | undefined)!]}
                            </Typography>
                          )}
                        </>
                      ) : (
                        <Typography component="em" variant="body1" color="text.secondary" sx={{ fontStyle: 'italic', display: 'block' }}>
                          Available after your manager submits their review.
                        </Typography>
                      )}
                    </Paper>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          );
        })()}

        {/* Objective Measures */}
        {review.objectiveRatings && review.objectiveRatings.length > 0 && (() => {
          const peerSummary = review.anonymousPeerFeedback;
          const showPeerAverageColumn = Boolean(peerSummary?.isVisible);
          const peerAverageByMeasureId = new Map(
            (peerSummary?.objectiveAverages ?? []).map((a) => [a.objectiveMeasureId, a])
          );
          return (
          <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.08)', borderRadius: 2, mb: 3 }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h6" fontWeight="600" gutterBottom>
                Objective Measures
              </Typography>
              {showPeerAverageColumn && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Peer averages are shown when at least {peerSummary!.visibilityThreshold} peers have rated
                  a measure (currently {peerSummary!.submittedPeerCount} peer
                  {peerSummary!.submittedPeerCount === 1 ? '' : 's'} submitted overall). Individual peer
                  ratings and names stay private.
                </Alert>
              )}
              <TableContainer sx={{ mt: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Measure</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }} align="center">Employee Rating</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Employee Comment</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }} align="center">Manager Rating</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Manager Comment</TableCell>
                      {showPeerAverageColumn && (
                        <TableCell sx={{ fontWeight: 'bold' }} align="center">Peer average</TableCell>
                      )}
                      <TableCell sx={[{ fontWeight: 'bold', width: 56 }, printHiddenSx]} align="right" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {review.objectiveRatings.map((rating) => {
                      const empComment = truncateReviewComment(rating.employeeComment, REVIEW_COMMENT_DISPLAY_MAX_LEN);
                      const mgrComment = truncateReviewComment(rating.managerComment, REVIEW_COMMENT_DISPLAY_MAX_LEN);
                      const peerAvg = peerAverageByMeasureId.get(rating.objectiveMeasureId);
                      return (
                        <TableRow key={rating.id} hover>
                          <TableCell sx={{ fontWeight: 500 }}>{rating.objectiveMeasureName || 'Unknown'}</TableCell>
                          <TableCell align="center">
                            {normaliseRating(rating.employeeRating) != null ? (
                              <Chip
                                label={String(normaliseRating(rating.employeeRating))}
                                size="small"
                                sx={{
                                  bgcolor: getRatingColor(normaliseRating(rating.employeeRating)),
                                  color: 'white',
                                  minWidth: 28,
                                  height: 28,
                                  borderRadius: '50%',
                                  '& .MuiChip-label': { px: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' },
                                }}
                              />
                            ) : (
                              <Typography variant="body2" color="text.secondary">N/A</Typography>
                            )}
                          </TableCell>
                          <CommentTableCell truncated={empComment} />
                          <TableCell align="center">
                            {normaliseRating(rating.managerRating) != null ? (
                              <Chip
                                label={String(normaliseRating(rating.managerRating))}
                                size="small"
                                sx={{
                                  bgcolor: getRatingColor(normaliseRating(rating.managerRating)),
                                  color: 'white',
                                  minWidth: 28,
                                  height: 28,
                                  borderRadius: '50%',
                                  '& .MuiChip-label': { px: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' },
                                }}
                              />
                            ) : (
                              <Typography variant="body2" color="text.secondary">N/A</Typography>
                            )}
                          </TableCell>
                          <CommentTableCell truncated={mgrComment} />
                          {showPeerAverageColumn && (
                            <TableCell align="center">
                              {peerAvg?.hasEnoughResponses && peerAvg.averageRating != null ? (
                                <Typography variant="body1" fontWeight={600}>
                                  {peerAvg.averageRating.toFixed(1)}
                                </Typography>
                              ) : (
                                <Typography variant="body2" color="text.secondary" component="em" sx={{ fontStyle: 'italic' }}>
                                  Not enough peer responses
                                </Typography>
                              )}
                            </TableCell>
                          )}
                          <TableCell align="right" padding="none" sx={printHiddenSx}>
                            <Tooltip title="View details">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => setMeasureDetailRating(rating)}
                                aria-label="View measure details"
                              >
                                <VisibilityIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
          );
        })()}

      {/* Objective measure detail modal */}
      <Dialog
        open={measureDetailRating != null}
        onClose={() => setMeasureDetailRating(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle>
          {measureDetailRating?.objectiveMeasureName || 'Objective measure'}
        </DialogTitle>
        <DialogContent dividers>
          {measureDetailRating && (
            <Box>
              <Typography variant="subtitle2" fontWeight="600" gutterBottom sx={{ mt: 0 }}>
                Rating criteria
              </Typography>
              <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                  <strong>Level 1 (Development Required):</strong> {getLevelCriteria(measureDetailRating, 1)}
                </Typography>
                <Typography variant="body2" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                  <strong>Level 2 (Meets Expectation):</strong> {getLevelCriteria(measureDetailRating, 2)}
                </Typography>
                <Typography variant="body2" color="text.secondary" display="block">
                  <strong>Level 3 (Exceeds Expectation):</strong> {getLevelCriteria(measureDetailRating, 3)}
                </Typography>
              </Box>
              <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                Employee
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography component="span" variant="body2" color="text.secondary" gutterBottom display="block">
                  Rating: {normaliseRating(measureDetailRating.employeeRating) != null ? (
                    <Chip
                      label={String(normaliseRating(measureDetailRating.employeeRating))}
                      size="small"
                      sx={{
                        bgcolor: getRatingColor(normaliseRating(measureDetailRating.employeeRating)),
                        color: 'white',
                        verticalAlign: 'middle',
                      }}
                    />
                  ) : (
                    'N/A'
                  )}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Comment: {measureDetailRating.employeeComment?.trim() || 'No comment'}
                </Typography>
              </Box>
              <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                Manager
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography component="span" variant="body2" color="text.secondary" gutterBottom display="block">
                  Rating: {normaliseRating(measureDetailRating.managerRating) != null ? (
                    <Chip
                      label={String(normaliseRating(measureDetailRating.managerRating))}
                      size="small"
                      sx={{
                        bgcolor: getRatingColor(normaliseRating(measureDetailRating.managerRating)),
                        color: 'white',
                        verticalAlign: 'middle',
                      }}
                    />
                  ) : (
                    'N/A'
                  )}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Comment: {measureDetailRating.managerComment?.trim() || 'No comment'}
                </Typography>
              </Box>
              {review.employeeId !== currentUser?.id &&
                (review?.peerObjectiveRatings?.filter((por) => por.objectiveMeasureId === measureDetailRating.objectiveMeasureId).length ?? 0) > 0 ? (
                <>
                  <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                    Peer feedback
                  </Typography>
                  {review.peerObjectiveRatings!
                    .filter((por) => por.objectiveMeasureId === measureDetailRating.objectiveMeasureId)
                    .map((por) => (
                      <Box key={por.id} sx={{ mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                        <Typography variant="body2" fontWeight="600" color="text.secondary" gutterBottom>
                          {por.raterName ?? 'Peer'}
                        </Typography>
                        <Typography component="span" variant="body2" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                          Rating: {normaliseRating(por.rating as number | string | undefined) != null ? (
                            <Chip
                              label={String(normaliseRating(por.rating as number | string | undefined))}
                              size="small"
                              sx={{
                                bgcolor: getRatingColor(normaliseRating(por.rating as number | string | undefined)),
                                color: 'white',
                                verticalAlign: 'middle',
                              }}
                            />
                          ) : (
                            'N/A'
                          )}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Comment: {por.comment?.trim() || 'No comment'}
                        </Typography>
                      </Box>
                    ))}
                </>
              ) : review.employeeId === currentUser?.id && review.anonymousPeerFeedback?.isVisible ? (
                (() => {
                  const peerAvg = review.anonymousPeerFeedback.objectiveAverages.find(
                    (a) => a.objectiveMeasureId === measureDetailRating.objectiveMeasureId
                  );
                  return (
                    <>
                      <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                        Peer average
                      </Typography>
                      <Box sx={{ mb: 2 }}>
                        {peerAvg?.hasEnoughResponses && peerAvg.averageRating != null ? (
                          <Typography variant="body2" color="text.secondary">
                            Average rating from peers: <strong>{peerAvg.averageRating.toFixed(1)}</strong>
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary" component="em" sx={{ fontStyle: 'italic' }}>
                            Not enough peer responses for this measure to show an average yet
                            (need at least {review.anonymousPeerFeedback.visibilityThreshold}).
                          </Typography>
                        )}
                      </Box>
                    </>
                  );
                })()
              ) : null}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMeasureDetailRating(null)} sx={{ textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>

        {/* Behavioural measures – always show so when HR adds measures no code change is needed */}
        <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.08)', borderRadius: 2, mb: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" fontWeight="600" gutterBottom>
              Behavioural measures
            </Typography>
            {review.behaviouralRatings && review.behaviouralRatings.length > 0 ? (
              <TableContainer sx={{ mt: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Measure</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }} align="center">Employee rating</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Employee comment</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }} align="center">Manager rating</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Manager comment</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {review.behaviouralRatings.map((rating) => {
                      const empComment = truncateReviewComment(rating.employeeComment, REVIEW_COMMENT_DISPLAY_MAX_LEN);
                      const mgrComment = truncateReviewComment(rating.managerComment, REVIEW_COMMENT_DISPLAY_MAX_LEN);
                      return (
                        <TableRow key={rating.id} hover>
                          <TableCell sx={{ fontWeight: 500 }}>{rating.behaviouralMeasureName || 'Unknown'}</TableCell>
                          <TableCell align="center">
                            {normaliseRating(rating.employeeRating) != null ? (
                              <Chip
                                label={String(normaliseRating(rating.employeeRating))}
                                size="small"
                                sx={{
                                  bgcolor: getRatingColor(normaliseRating(rating.employeeRating)),
                                  color: 'white',
                                  minWidth: 28,
                                  height: 28,
                                  borderRadius: '50%',
                                  '& .MuiChip-label': { px: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' },
                                }}
                              />
                            ) : (
                              <Typography variant="body2" color="text.secondary">N/A</Typography>
                            )}
                          </TableCell>
                          <CommentTableCell truncated={empComment} />
                          <TableCell align="center">
                            {normaliseRating(rating.managerRating) != null ? (
                              <Chip
                                label={String(normaliseRating(rating.managerRating))}
                                size="small"
                                sx={{
                                  bgcolor: getRatingColor(normaliseRating(rating.managerRating)),
                                  color: 'white',
                                  minWidth: 28,
                                  height: 28,
                                  borderRadius: '50%',
                                  '& .MuiChip-label': { px: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' },
                                }}
                              />
                            ) : (
                              <Typography variant="body2" color="text.secondary">N/A</Typography>
                            )}
                          </TableCell>
                          <CommentTableCell truncated={mgrComment} />
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                No behavioural measures configured for this review.
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* Peer behavioural feedback (ratings + comments from peers) — never shown to employee */}
        {review.employeeId !== currentUser?.id && review.peerRatings && review.peerRatings.length > 0 && (
          <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.08)', borderRadius: 2, mb: 3 }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h6" fontWeight="600" gutterBottom>
                Peer behavioural feedback
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Feedback from peers on behavioural measures.
              </Typography>
              <TableContainer sx={{ mt: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Measure</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Rater</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }} align="center">Rating</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Comment</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {review.peerRatings.map((pr) => (
                      <TableRow key={pr.id ?? `${pr.raterId}-${pr.behaviouralMeasureId}`}>
                        <TableCell sx={{ fontWeight: 500 }}>{pr.behaviouralMeasureName || 'Unknown'}</TableCell>
                        <TableCell>{pr.raterName ?? 'Peer'}</TableCell>
                        <TableCell align="center">
                          {normaliseRating(pr.rating as number | string | undefined) != null ? (
                            <Chip
                              label={String(normaliseRating(pr.rating as number | string | undefined))}
                              size="small"
                              sx={{
                                bgcolor: getRatingColor(normaliseRating(pr.rating as number | string | undefined)),
                                color: 'white',
                                minWidth: 28,
                                height: 28,
                                borderRadius: '50%',
                                '& .MuiChip-label': { px: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' },
                              }}
                            />
                          ) : (
                            <Typography variant="body2" color="text.secondary">N/A</Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 280 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                            {pr.comment?.trim() || '—'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}

        {/* Development Areas & Plans */}
        {(review.employeeDevelopmentAreas || review.managerOverallAssessment) && (
          <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.08)', borderRadius: 2, mb: 3 }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h6" fontWeight="600" gutterBottom>
                Development & Assessment
              </Typography>
              {review.employeeDevelopmentAreas && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                    Employee Development Areas
                  </Typography>
                  <Paper sx={{ p: 2, bgcolor: '#f5f7fa' }}>
                    <Typography variant="body2">{review.employeeDevelopmentAreas}</Typography>
                  </Paper>
                </Box>
              )}
              {review.managerOverallAssessment && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                    Manager Overall Assessment
                  </Typography>
                  <Paper sx={{ p: 2, bgcolor: '#f5f7fa' }}>
                    <Typography variant="body2">{review.managerOverallAssessment}</Typography>
                  </Paper>
                </Box>
              )}
            </CardContent>
          </Card>
        )}

        {/* Individual Development Plan Section — excluded from print (IDP detail is out of scope for print export). */}
        {review && (
          <Card
            sx={{
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              borderRadius: 2,
              mb: 3,
              '@media print': { display: 'none !important' },
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h6" fontWeight="600" gutterBottom>
                Individual Development Plan
              </Typography>
              {idpQuarterlyReviewLoading ? (
                <Box display="flex" justifyContent="center" py={2}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <Box mt={2}>
                  {idpQuarterlyReview?.isCompleted ? (
                    <Alert severity="success" sx={{ mb: 2 }}>
                      Quarterly review completed. Both manager and employee have reviewed the IDP for this review cycle.
                    </Alert>
                  ) : (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Remember to revisit the Individual Development Plan (IDP) on a regular basis. You can review and update it at any time.
                    </Alert>
                  )}
                  <Button
                    variant="outlined"
                    onClick={() => {
                      const year = review.reviewCycleName?.match(/\b(20\d{2})\b/)?.[1] ?? new Date().getFullYear();
                      navigate(`/individual-development-plan/${review.employeeId}/${year}`);
                    }}
                    sx={{ mt: 2 }}
                  >
                    View IDP
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        )}

        {/* Peer nominations (when peer reviews enabled) */}
        {peerReviewSettings.allowPeerReviews && (
          <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.08)', borderRadius: 2, mb: 3 }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                <Typography variant="h6" fontWeight="600">
                  Peer reviewers
                </Typography>
                {canAddPeerReviewers() && (
                  <Box sx={printHiddenSx}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<PersonAddIcon />}
                      onClick={() => {
                        setAddPeerSelectedPeers([]);
                        setAddPeerSelectedObjectiveIds(review?.peerObjectiveMeasureIds ?? []);
                        setAddPeerError('');
                        setAddPeerDialogOpen(true);
                      }}
                      sx={{ textTransform: 'none' }}
                    >
                      Add peer reviewers
                    </Button>
                  </Box>
                )}
              </Box>
              {review.peerNominations && review.peerNominations.length > 0 ? (
                <TableContainer sx={{ mt: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Peer</TableCell>
                        {review.employeeId !== currentUser?.id && (
                          <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                        )}
                        <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {review.peerNominations.map((pn) => {
                        const pending = !pn.isApproved && !pn.isRejected;
                        const canSeeSubmissionStatus = review.employeeId !== currentUser?.id;
                        return (
                          <TableRow key={pn.id}>
                            <TableCell>{pn.nominatedPeerName || 'Unknown'}</TableCell>
                            {canSeeSubmissionStatus && (
                              <TableCell>
                                {pn.nominatedPeerEmail ? (
                                  <Link href={`mailto:${pn.nominatedPeerEmail}`} underline="hover">
                                    {pn.nominatedPeerEmail}
                                  </Link>
                                ) : (
                                  <Typography variant="body2" color="text.secondary">—</Typography>
                                )}
                              </TableCell>
                            )}
                            <TableCell>
                              {pending && <Chip label="Pending approval" size="small" color="default" />}
                              {pn.isRejected && (
                                <Box>
                                  <Chip label="Rejected" size="small" color="error" title={pn.rejectionReason} />
                                  {pn.rejectionReason && (
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                      Reason: {pn.rejectionReason}
                                    </Typography>
                                  )}
                                </Box>
                              )}
                              {pn.isApproved && !pn.isRejected && (
                                canSeeSubmissionStatus ? (
                                  pn.hasResponded ? (
                                    <Chip label="Submitted" size="small" color="success" />
                                  ) : (
                                    <Chip label="Awaiting feedback" size="small" color="warning" />
                                  )
                                ) : (
                                  <Chip label="Invited" size="small" color="success" />
                                )
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  {review.employeeId && currentUser?.id === review.employeeId
                    ? 'Peer feedback is collected as part of this review. Your manager can see who has been invited and who has responded.'
                    : 'No peer reviewers nominated yet. Use "Submit review" to add peer reviewers and complete your self-review.'}
                </Typography>
              )}
            </CardContent>
          </Card>
        )}

        {/* Peer objective ratings (when peer review enabled and peers have submitted) — never shown to employee */}
        {review.employeeId !== currentUser?.id && review.peerObjectiveRatings && review.peerObjectiveRatings.length > 0 && (
          <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.08)', borderRadius: 2, mb: 3 }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h6" fontWeight="600" gutterBottom>
                Peer objective ratings
              </Typography>
              <TableContainer sx={{ mt: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Objective</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Rater</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }} align="center">Rating</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Comment</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {review.peerObjectiveRatings.map((por) => (
                      <TableRow key={por.id}>
                        <TableCell>{por.objectiveMeasureName}</TableCell>
                        <TableCell>{por.raterName ?? 'Peer'}</TableCell>
                        <TableCell align="center">
                          {normaliseRating(por.rating as number | string | undefined) != null ? (
                            <Chip
                              label={String(normaliseRating(por.rating as number | string | undefined))}
                              size="small"
                              sx={{
                                bgcolor: getRatingColor(normaliseRating(por.rating as number | string | undefined)),
                                color: 'white',
                                minWidth: 28,
                                height: 28,
                                borderRadius: '50%',
                                '& .MuiChip-label': { px: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' },
                              }}
                            />
                          ) : (
                            '—'
                          )}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 280 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                            {por.comment || '—'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}

        {/* Supporting Documents — excluded from print (attachments out of scope). */}
        <Card
          sx={{
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            borderRadius: 2,
            mb: 3,
            '@media print': { display: 'none !important' },
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" fontWeight="600">
                Supporting Documents
              </Typography>
              {canUploadSupportingDocuments() && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AttachFileIcon />}
                  onClick={() => setUploadDocDialogOpen(true)}
                  sx={{ textTransform: 'none' }}
                >
                  Upload Document
                </Button>
              )}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Upload meeting notes or other supporting documentation. Both you and your manager can add documents until the discussion is acknowledged or the review is finalised.
            </Typography>
            {!(review.supportingDocuments?.length) ? (
              <Typography variant="body2" color="text.secondary">
                No documents yet. Use "Upload Document" to add supporting files.
              </Typography>
            ) : (
              <SupportingDocumentsTable
                documents={review.supportingDocuments.map((d) => ({
                  id: d.id,
                  fileName: d.fileName,
                  description: d.description,
                  uploadedByName: d.uploadedByName,
                  uploadedAt: d.uploadedAt,
                  fileSize: d.fileSize,
                  contentType: d.contentType,
                }))}
                onView={handleViewSupportingDocument}
                onDownload={handleDownloadSupportingDocument}
                onDelete={(documentId, fileName) => setDeleteConfirmDoc({ documentId, fileName })}
                canEdit={canUploadSupportingDocuments()}
                onEdit={(documentId, fileName, description) => {
                  setEditingDoc({ id: documentId, fileName, description });
                  setEditDocDialogOpen(true);
                }}
                formatDate={(date) => formatDate(date, { includeTime: true })}
              />
            )}
          </CardContent>
        </Card>

        {/* Review Metadata */}
        <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.08)', borderRadius: 2, mb: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" fontWeight="600" gutterBottom>
              Review Timeline
            </Typography>
            <Box sx={{ mt: 2 }} key={displayTimeZoneId}>
              {review.createdAt && (
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Review Cycle Started:</strong>{' '}
                  {formatDate(review.createdAt, { includeTime: true })}
                </Typography>
              )}
              {review.selfReviewSubmittedAt && (
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Self Review Submitted:</strong>{' '}
                  {formatDate(review.selfReviewSubmittedAt, { includeTime: true })}
                </Typography>
              )}
              {review.managerReviewSubmittedAt && (
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Manager Review Submitted:</strong>{' '}
                  {formatDate(review.managerReviewSubmittedAt, { includeTime: true })}
                </Typography>
              )}
              {review.discussionDate && (
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Discussion Date:</strong> {formatDate(review.discussionDate)}
                </Typography>
              )}
              {review.actualDiscussionDate && (
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Actual Discussion Date:</strong> {formatDate(review.actualDiscussionDate)}
                </Typography>
              )}
              {review.discussionAcknowledgedAt && (
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Discussion Acknowledged:</strong> {formatDate(review.discussionAcknowledgedAt, { includeTime: true })}
                </Typography>
              )}
              {review.finalisedAt && (
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Finalised:</strong> {formatDate(review.finalisedAt, { includeTime: true })}
                </Typography>
              )}
              {(review.managerReviewedIdpAt || review.employeeAcknowledgedIdpReviewAt) && (
                <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider', '@media print': { display: 'none' } }}>
                  <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                    IDP reviewed as part of this review
                  </Typography>
                  {review.managerReviewedIdpAt && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Manager confirmed IDP reviewed:</strong> {formatDate(review.managerReviewedIdpAt, { includeTime: true })}
                    </Typography>
                  )}
                  {review.managerIdpReviewComments && (
                    <Typography variant="body2" color="text.secondary" gutterBottom sx={{ whiteSpace: 'pre-wrap' }}>
                      <strong>Manager IDP comments:</strong> {review.managerIdpReviewComments}
                    </Typography>
                  )}
                  {review.employeeAcknowledgedIdpReviewAt && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Employee acknowledged IDP review:</strong> {formatDate(review.employeeAcknowledgedIdpReviewAt, { includeTime: true })}
                    </Typography>
                  )}
                  {review.employeeIdpReviewComments && (
                    <Typography variant="body2" color="text.secondary" gutterBottom sx={{ whiteSpace: 'pre-wrap' }}>
                      <strong>Employee IDP comments:</strong> {review.employeeIdpReviewComments}
                    </Typography>
                  )}
                </Box>
              )}
              {currentUser?.roles?.some((r: string) => r === 'Administrator' || r === 'SeniorManager') && review.feedbackAttestation != null && (
                <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider', bgcolor: 'action.hover', p: 2, borderRadius: 1 }}>
                  <Typography variant="subtitle2" fontWeight="600" gutterBottom color="text.secondary">
                    Feedback attestation (admin / senior manager only – manager does not see this)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>How much feedback did the employee receive during this cycle?</strong>{' '}
                    {review.feedbackAttestation === 0
                      ? 'Regular feedback received'
                      : review.feedbackAttestation === 1
                        ? 'Some feedback received (limited/inconsistent)'
                        : review.feedbackAttestation === 2
                          ? 'Little to no feedback received / poor interaction'
                          : '—'}
                  </Typography>
                  {(review.feedbackAttestation === 1 || review.feedbackAttestation === 2) && review.feedbackAttestationComment && (
                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                      <strong>Employee comment:</strong> {review.feedbackAttestationComment}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Audit Trail */}
        <Accordion sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.08)', borderRadius: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6" fontWeight="600">
              Audit Trail
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {loadingAudit ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : auditEvents.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No audit events found.
              </Typography>
            ) : (
              <Box>
                {auditEvents.map((event) => (
                  <Box key={event.id} sx={{ mb: 2, pb: 2, borderBottom: '1px solid #eee' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="subtitle2">
                        {getReviewEventTypeLabel(event.eventType)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(event.eventAt, { includeTime: true })}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      By: {event.actorName}
                    </Typography>
                    {event.details && (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {event.details}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      </Box>

      <ManagerReviewDialog
        open={managerReviewDialogOpen}
        onClose={() => setManagerReviewDialogOpen(false)}
        review={review}
        onSubmit={handleSubmitManagerReview}
        submitting={submitting}
        submitError={submitError}
        onDismissError={() => setSubmitError('')}
      />

      {/* Peer Rating Dialog */}
      <Dialog
        open={peerRatingDialogOpen}
        onClose={() => setPeerRatingDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { maxHeight: '90vh' }
        }}
      >
        <DialogTitle>Provide Peer Feedback</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            You must address every behavioural competency below: provide a rating and comment, or mark as N/A with a reason. Rate each competency based on your interactions with {review?.employeeName || 'this employee'}.
          </Alert>
          {submitError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSubmitError('')}>
              {submitError}
            </Alert>
          )}

          {peerRatings.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No behavioural measures to rate at this time.
            </Typography>
          ) : peerRatings.map((rating, index) => (
            <Box key={rating.behaviouralMeasureId} sx={{ mb: 2 }}>
              <BehaviouralMeasureRatingCard
                measureName={rating.behaviouralMeasureName}
                value={typeof rating.rating === 'number' ? rating.rating : 0}
                onRatingChange={(val) => {
                  const newRatings = [...peerRatings];
                  newRatings[index] = { ...newRatings[index], rating: val };
                  setPeerRatings(newRatings);
                }}
                comment={rating.comment}
                onCommentChange={(val) => {
                  const newRatings = [...peerRatings];
                  newRatings[index] = { ...newRatings[index], comment: val };
                  setPeerRatings(newRatings);
                }}
                ratingLabel="Rating *"
                commentRequired
              />
              <Button
                variant="outlined"
                onClick={() => handleSubmitPeerRating(rating.behaviouralMeasureId)}
                disabled={submitting}
                sx={{ mt: 1 }}
                size="small"
              >
                {submitting ? <CircularProgress size={20} /> : 'Save Rating'}
              </Button>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPeerRatingDialogOpen(false)} sx={{ textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Peer objective ratings dialog (approved peer submits ratings for objectives in scope) */}
      <Dialog
        open={peerObjectiveRatingDialogOpen}
        onClose={() => setPeerObjectiveRatingDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Start Peer Rating</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Rate the following objectives for {review?.employeeName ?? 'this employee'}. Your manager will see your ratings.
          </Typography>
          {peerObjectiveSubmitError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPeerObjectiveSubmitError('')}>
              {peerObjectiveSubmitError}
            </Alert>
          )}
          {peerObjectiveRatingForm.map((row, index) => (
            <ObjectiveMeasureRatingCard
              key={row.objectiveMeasureId}
              measureName={row.objectiveMeasureName}
              level1Criteria="—"
              level2Criteria="—"
              level3Criteria="—"
              value={row.rating || null}
              onRatingChange={(val) => {
                const next = peerObjectiveRatingForm.map((r, i) =>
                  i === index ? { ...r, rating: val ?? 0 } : r
                );
                setPeerObjectiveRatingForm(next);
              }}
              comment={row.comment}
              onCommentChange={(val) => {
                const next = peerObjectiveRatingForm.map((r, i) =>
                  i === index ? { ...r, comment: val } : r
                );
                setPeerObjectiveRatingForm(next);
              }}
              error={false}
              ratingLabel="Rating *"
              allowNa={false}
            />
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPeerObjectiveRatingDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmitPeerObjectiveRatings} disabled={peerObjectiveSubmitting} sx={{ textTransform: 'none' }}>
            {peerObjectiveSubmitting ? <CircularProgress size={24} /> : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Acknowledgment Dialog */}
      <Dialog
        open={acknowledgmentDialogOpen}
        onClose={() => { setAcknowledgmentConfirmed(false); setAcknowledgmentDialogOpen(false); }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Acknowledge Discussion</DialogTitle>
        <DialogContent>
          {submitError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSubmitError('')}>
              {submitError}
            </Alert>
          )}
          
          {/* Normal Acknowledgment Section */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Please acknowledge that you have reviewed and discussed the manager's feedback.
              {review && review.status !== 'finalised' && review.status !== '6' && (
                <> If you have meeting notes, please upload them as supporting documents <strong>before</strong> acknowledging.</>
              )}
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <AppDatePicker
                label="Actual Discussion Date"
                value={actualDiscussionDate}
                onChange={(d) => setActualDiscussionDate(d)}
                maxDate={new Date()}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    margin: 'normal',
                    helperText: 'Date when the discussion actually took place (optional)',
                  },
                }}
                shouldDisableDate={shouldDisableDate}
              />
            </LocalizationProvider>
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Meeting Notes:</strong> If you have meeting notes or documentation from the discussion, please upload them as supporting documents using the document upload feature.
              </Typography>
            </Alert>

            <Typography variant="subtitle2" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
              By acknowledging, you confirm the following:
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 1 } }}>
              <Typography component="li" variant="body2">
                I confirm that I have participated in the performance review discussion and that the information recorded reflects, to the best of my knowledge, an accurate summary of the discussions relating to my rating with my manager/ team leader.
              </Typography>
              <Typography component="li" variant="body2">
                I understand that this acknowledgement confirms that the review discussion has occurred, and does not necessarily indicate agreement with the evaluation, ratings, or comments recorded.
              </Typography>
              <Typography component="li" variant="body2">
                I understand that I have the right to Dispute this Review.
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={acknowledgmentConfirmed}
                  onChange={(e) => setAcknowledgmentConfirmed(e.target.checked)}
                  color="primary"
                />
              }
              label="I have read and confirm the above"
              sx={{ mt: 2, display: 'block' }}
            />
            <TextField
              fullWidth
              label="IDP review comments (optional)"
              value={employeeIdpReviewComments}
              onChange={(e) => setEmployeeIdpReviewComments(e.target.value)}
              margin="normal"
              multiline
              rows={2}
              placeholder="Any specific points about your IDP discussed in this review..."
              sx={{ mt: 2 }}
            />

            <Typography variant="subtitle2" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
              Feedback attestation (required)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              How much feedback did you receive from your manager during this review cycle? This is used for organisational insight only; your manager will not see your answer.
            </Typography>
            <FormControl component="fieldset" required sx={{ mt: 1, display: 'block' }}>
              <RadioGroup
                value={feedbackAttestation !== null ? feedbackAttestation : ''}
                onChange={(e) => setFeedbackAttestation(parseInt(e.target.value, 10))}
              >
                <FormControlLabel value={0} control={<Radio />} label="Regular feedback received" />
                <FormControlLabel value={1} control={<Radio />} label="Some feedback received (limited/inconsistent)" />
                <FormControlLabel value={2} control={<Radio />} label="Little to no feedback received / poor interaction" />
              </RadioGroup>
            </FormControl>
            {(feedbackAttestation === 1 || feedbackAttestation === 2) && (
              <TextField
                fullWidth
                label="Comment (required for this selection)"
                value={feedbackAttestationComment}
                onChange={(e) => setFeedbackAttestationComment(e.target.value)}
                margin="normal"
                multiline
                rows={2}
                required
                error={feedbackAttestation !== null && (feedbackAttestation === 1 || feedbackAttestation === 2) && !feedbackAttestationComment.trim()}
                helperText={(feedbackAttestation === 1 || feedbackAttestation === 2) && !feedbackAttestationComment.trim() ? 'Please add a comment.' : undefined}
                sx={{ mt: 1 }}
              />
            )}
          </Box>

          {/* Dispute Section */}
          <Box sx={{ 
            border: '2px solid', 
            borderColor: 'warning.main', 
            borderRadius: 2, 
            p: 2, 
            bgcolor: 'warning.50',
            mt: 3
          }}>
            <Typography variant="h6" color="warning.dark" sx={{ mb: 1, fontWeight: 600 }}>
              Dispute This Review
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              If you disagree with your review and wish to formally dispute it, you can initiate a dispute process. This will trigger a formal HR review.
            </Typography>
            <Button
              variant="outlined"
              color="warning"
              onClick={() => {
                setAcknowledgmentDialogOpen(false);
                setDisputeDialogOpen(true);
              }}
              sx={{ textTransform: 'none' }}
            >
              Dispute This Review
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setAcknowledgmentConfirmed(false);
              setFeedbackAttestation(null);
              setFeedbackAttestationComment('');
              setAcknowledgmentDialogOpen(false);
            }}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAcknowledgeDiscussion}
            variant="contained"
            disabled={
              submitting
              || !acknowledgmentConfirmed
              || feedbackAttestation === null
              || ((feedbackAttestation === 1 || feedbackAttestation === 2) && !feedbackAttestationComment.trim())
            }
            sx={{ textTransform: 'none' }}
          >
            {submitting ? <CircularProgress size={24} /> : 'Acknowledge'}
          </Button>
        </DialogActions>
      </Dialog>

      <DisputeDialog
        open={disputeDialogOpen}
        onClose={() => {
          setDisputeDialogOpen(false);
          setSubmitError('');
        }}
        onSubmit={handleDisputeReview}
        submitting={submitting}
        submitError={submitError}
        onDismissError={() => setSubmitError('')}
      />

      {/* Exclude Request Dialog */}
      <ExcludeDialog
        open={excludeDialogOpen}
        onClose={() => {
          setExcludeDialogOpen(false);
          setSubmitError('');
        }}
        onSubmit={handleRequestExclude}
        submitting={submitting}
        submitError={submitError}
        onDismissError={() => setSubmitError('')}
      />

      {/* Exclude Approve/Deny Confirmation */}
      <Dialog
        open={excludeConfirmDialog !== null}
        onClose={() => setExcludeConfirmDialog(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {excludeConfirmDialog === 'approve' ? 'Approve Exclude Request' : 'Deny Exclude Request'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            {excludeConfirmDialog === 'approve'
              ? 'Are you sure you want to approve closing this review without a rating? The review will be marked as excluded.'
              : 'Are you sure you want to deny this exclude request? The employee will need to complete the review.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExcludeConfirmDialog(null)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={excludeConfirmDialog === 'approve' ? handleApproveExclude : handleDenyExclude}
            variant="contained"
            color={excludeConfirmDialog === 'approve' ? 'success' : 'inherit'}
            disabled={submitting}
            sx={{ textTransform: 'none' }}
          >
            {excludeConfirmDialog === 'approve' ? 'Approve' : 'Deny'}
          </Button>
        </DialogActions>
      </Dialog>

      <DocumentUploadDialog
        open={uploadDocDialogOpen}
        onClose={() => setUploadDocDialogOpen(false)}
        onUpload={handleUploadSupportingDocument}
        uploading={uploadingDoc}
      />

      <Dialog
        open={editDocDialogOpen}
        onClose={() => { setEditDocDialogOpen(false); setEditingDoc(null); setSubmitError(''); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Document</DialogTitle>
        <DialogContent>
          {editingDoc && (
            <>
              {submitError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSubmitError('')}>
                  {submitError}
                </Alert>
              )}
              <TextField
                fullWidth
                label="File name"
                value={editingDoc.fileName}
                onChange={(e) => setEditingDoc({ ...editingDoc, fileName: e.target.value })}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label="Description (optional)"
                value={editingDoc.description}
                onChange={(e) => setEditingDoc({ ...editingDoc, description: e.target.value })}
                margin="normal"
                multiline
                rows={3}
              />
              <DialogActions sx={{ mt: 2, px: 0 }}>
                <Button onClick={() => { setEditDocDialogOpen(false); setEditingDoc(null); setSubmitError(''); }} sx={{ textTransform: 'none' }}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={() => editingDoc && handleUpdateSupportingDocument(editingDoc.id, editingDoc.fileName, editingDoc.description)}
                  disabled={!editingDoc?.fileName.trim()}
                  sx={{ textTransform: 'none' }}
                >
                  Save
                </Button>
              </DialogActions>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!deleteConfirmDoc}
        onClose={() => setDeleteConfirmDoc(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Document</DialogTitle>
        <DialogContent>
          {deleteConfirmDoc && (
            <Typography variant="body1">
              Delete "{deleteConfirmDoc.fileName}"? This cannot be undone.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteConfirmDoc(null)} disabled={!!deletingDocId} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={!!deletingDocId}
            onClick={() => deleteConfirmDoc && handleDeleteSupportingDocument(deleteConfirmDoc.documentId, deleteConfirmDoc.fileName)}
            sx={{ textTransform: 'none' }}
          >
            {deletingDocId ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!previewNotAvailableFileName} onClose={() => setPreviewNotAvailableFileName(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Preview not available</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This file type cannot be displayed in the browser. Please use Download to open it on your device.
          </Typography>
          {previewNotAvailableFileName && (
            <Typography variant="body2" sx={{ mt: 1 }} fontWeight={500}>
              File: {previewNotAvailableFileName}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewNotAvailableFileName(null)} sx={{ textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Withdraw Dispute Confirmation Dialog */}
      <Dialog
        open={withdrawDisputeDialogOpen}
        onClose={() => setWithdrawDisputeDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Withdraw Dispute</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to withdraw your dispute? This will revert the review to the normal acknowledgment process.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWithdrawDisputeDialogOpen(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button onClick={handleWithdrawDispute} variant="contained" color="warning" disabled={submitting} sx={{ textTransform: 'none' }}>
            {submitting ? <CircularProgress size={24} /> : 'Withdraw Dispute'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Peer Reviewers Dialog */}
      <Dialog
        open={addPeerDialogOpen}
        onClose={() => {
          setAddPeerDialogOpen(false);
          setAddPeerSelectedPeers([]);
          setAddPeerSelectedObjectiveIds([]);
          setAddPeerError('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Peer Reviewers</DialogTitle>
        <DialogContent>
          {addPeerError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setAddPeerError('')}>
              {addPeerError}
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select peers to add as reviewers. They will be notified by email and can submit feedback on the objectives you select below. The employee is not notified.
          </Typography>
          <Autocomplete
            multiple
            options={addPeerOptions}
            getOptionLabel={(opt) => opt.fullName}
            value={addPeerSelectedPeers}
            onChange={(_, newValue) => setAddPeerSelectedPeers(newValue)}
            loading={addPeerOptionsLoading}
            renderInput={(params) => (
              <TextField {...params} label="Select peers" placeholder="Search by name" />
            )}
            filterSelectedOptions
            sx={{ mt: 1 }}
          />
          {review?.objectiveRatings && review.objectiveRatings.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" fontWeight="600" sx={{ mb: 1 }}>
                Objectives for peer review (select at least one) *
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ maxWidth: 520 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" sx={{ width: 48 }}>
                        <Checkbox
                          indeterminate={
                            addPeerSelectedObjectiveIds.length > 0 &&
                            addPeerSelectedObjectiveIds.length < review.objectiveRatings.length
                          }
                          checked={
                            review.objectiveRatings.length > 0 &&
                            review.objectiveRatings.every((r) => addPeerSelectedObjectiveIds.includes(r.objectiveMeasureId))
                          }
                          onChange={(e) => {
                            if (e.target.checked) {
                              setAddPeerSelectedObjectiveIds(review.objectiveRatings.map((r) => r.objectiveMeasureId));
                            } else {
                              setAddPeerSelectedObjectiveIds([]);
                            }
                            setAddPeerError('');
                          }}
                          aria-label="Select all objectives"
                        />
                      </TableCell>
                      <TableCell><strong>Objective</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {review.objectiveRatings.map((r) => {
                      const checked = addPeerSelectedObjectiveIds.includes(r.objectiveMeasureId);
                      return (
                        <TableRow key={r.objectiveMeasureId} hover>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={checked}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setAddPeerSelectedObjectiveIds((ids) => [...ids, r.objectiveMeasureId]);
                                } else {
                                  setAddPeerSelectedObjectiveIds((ids) => ids.filter((id) => id !== r.objectiveMeasureId));
                                }
                                setAddPeerError('');
                              }}
                              aria-label={`Select ${r.objectiveMeasureName ?? r.objectiveMeasureId}`}
                            />
                          </TableCell>
                          <TableCell>{r.objectiveMeasureName ?? r.objectiveMeasureId}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setAddPeerDialogOpen(false);
              setAddPeerSelectedPeers([]);
              setAddPeerSelectedObjectiveIds([]);
              setAddPeerError('');
            }}
            sx={{ textTransform: 'none' }}
            disabled={addPeerSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddPeerRaters}
            variant="contained"
            disabled={addPeerSubmitting || addPeerSelectedPeers.length === 0 || addPeerSelectedObjectiveIds.length === 0}
            sx={{ textTransform: 'none' }}
          >
            {addPeerSubmitting ? <CircularProgress size={24} /> : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reopen Review Confirmation Dialog */}
      <Dialog
        open={reopenConfirmDialogOpen}
        onClose={() => setReopenConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reopen Review</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to reopen this review? The review will move back to the previous state and can be finalised again when ready. This action is recorded in the audit trail.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReopenConfirmDialogOpen(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button onClick={handleReopenReview} variant="contained" color="warning" disabled={submitting} sx={{ textTransform: 'none' }}>
            {submitting ? <CircularProgress size={24} /> : 'Reopen Review'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Finalise Review Confirmation Dialog */}
      <Dialog
        open={finaliseConfirmDialogOpen}
        onClose={() => setFinaliseConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Finalise Review</DialogTitle>
        <DialogContent>
          {(() => {
            const outstandingPeers = review?.peerNominations?.filter(
              (pn) => pn.isApproved && !pn.isRejected && !pn.hasResponded
            ) ?? [];
            if (outstandingPeers.length > 0) {
              return (
                <>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    The following peer reviewer(s) have not yet submitted their feedback:
                  </Typography>
                  <Box component="ul" sx={{ mt: 0, mb: 2, pl: 2 }}>
                    {outstandingPeers.map((pn) => (
                      <li key={pn.id}>
                        <Typography variant="body2">{pn.nominatedPeerName || 'Unknown'}</Typography>
                      </li>
                    ))}
                  </Box>
                  <Typography variant="body1" sx={{ color: 'warning.main' }}>
                    If you finalise now, these outstanding peer reviews will not be included in this employee review and peers will no longer be able to add ratings.
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Where possible, allow more time for peer feedback up to the end of the current review cycle before finalising.
                  </Typography>
                </>
              );
            }
            return (
              <Typography variant="body1">
                Are you sure you want to finalise this review? This will mark it as complete and the review will become read-only for both you and the employee.
              </Typography>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFinaliseConfirmDialogOpen(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button onClick={handleFinaliseReview} variant="contained" color="success" disabled={submitting} sx={{ textTransform: 'none' }}>
            {submitting ? <CircularProgress size={24} /> : (review?.peerNominations?.filter((pn) => pn.isApproved && !pn.isRejected && !pn.hasResponded).length ? 'Finalise anyway' : 'Finalise Review')}
          </Button>
        </DialogActions>
      </Dialog>

      <Box sx={printHiddenSx}>
        {showLegacyHelp && (
          <ReviewHelpSidebar isExpanded={helpExpanded} onToggle={() => setHelpExpanded(!helpExpanded)} />
        )}
      </Box>
    </AppLayout>
  );
}
